import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Zap, Power, Usb, Terminal, Monitor, Volume2, Radio, Box, Layers, Plus, Save, Trash2, Edit, BookOpen, Download, Upload, FileText, CheckCircle, AlertCircle, Move, ChevronDown, ChevronLeft, ChevronRight, Circle, Square, Cpu, HardDrive, Network, Wifi, Cable, Plug, Battery, Lightbulb, Settings, Activity, Database, Server, Cloud, Lock, Unlock, Key, Shield, Eye, EyeOff, Bell, Star, Heart, Flag, Tag, Bookmark, Folder, File, Image, Video, Music, Mic, Headphones, Gamepad2, Tv, Laptop, Smartphone, Tablet, Watch, Camera, Printer, Mail, Phone, MessageSquare, Send, Share2, Link, Unlink, Copy, Scissors, FileCheck, FileX, FolderOpen, FolderPlus, Archive, Package, ShoppingCart, CreditCard, DollarSign, TrendingUp, BarChart, PieChart, LineChart, Calendar, Clock, Timer, BellRing, BellOff, VolumeX, Volume1, Bluetooth, WifiOff, Signal, SignalHigh, SignalLow, SignalZero, Router, PlusCircle, EthernetPort } from 'lucide-react';
import {
  PORT,
  GRID,
  inferLayoutGrid,
  layoutPortKey,
  parseFittedGridId,
  templateIdFromSimpleLayout,
} from '../../utils/portLayout';
import { EquipmentFace } from '../../types';
import { exportEquipment, downloadEquipmentFile, importEquipment } from '../../utils/equipmentExport';
import { equipmentDatabase } from '../../data/equipment';
import {
  allPortNamingSchemes,
  BUILT_IN_PORT_NAMING_SCHEMES,
  fillMissingPortNumbers,
  formatPortLabel,
  getPortNamingScheme,
  loadCustomPortNamingSchemes,
  nextUnusedPortNumber,
  NUMBERS_ONLY_SCHEME_ID,
  prefixForPortKind,
  saveCustomPortNamingSchemes,
  type PortNamingScheme,
} from '../../utils/portNamingSchemes';
import { applyPortKindChange, collectLayoutPorts, inferModelFromLayout, initElectricalDraft, finalizeDeviceModel, mergePortsIntoDraft, validateDeviceModel } from '../../engine/networkLogic';
import { portPinId } from '../../engine/networkLogic/electricalGraph';
import type { FacedPort } from '../../engine/networkLogic/layoutPorts';
import type { DeviceModel } from '../../types/deviceModel';
import { ElectricalModelPanel } from '../Equipment/ElectricalModelPanel';
import LayoutBlueprintCanvas from './LayoutBlueprint/LayoutBlueprintCanvas';
import ElectricalInspector from './LayoutBlueprint/ElectricalInspector';
import { BlueprintPortPips } from './LayoutBlueprint/ElectricalCardNode';
import { applyElectricalPreset, ELECTRICAL_PRESETS } from '../../data/deviceModels/electricalPresets';
import { PoeRj45Marks } from './PoeRj45Marks';
import { poeRj45Border, poeRj45Look, portHoverTooltip } from '../../utils/poeRj45Look';
import { PortHoverTooltip } from './PortHoverTooltip';
import {
  loadCustomPortTypes,
  saveCustomPortTypes,
  type CustomPortType,
} from '../../utils/customPortTypes';

type PortKind = 
  | 'rj45'      // Ethernet
  | 'sfp'       // Fiber
  | 'sfpPlus'   // SFP+ (10Gbps fiber)
  | 'ac'        // AC Power outlet
  | 'dc'        // DC Power terminal
  | 'pvPlus'
  | 'pvMinus'
  | 'batPlus'
  | 'batMinus'
  | 'usb'       // USB port
  | 'console'   // Console/Serial
  | 'hdmi'      // HDMI/DisplayPort
  | 'audio'     // Audio jack
  | 'coax'      // Coax/BNC/RF
  | 'custom'    // Custom/Other (base type)
  | `custom:${string}` // Custom port type with ID
  | 'empty'    // Empty cell (cleared port slot)
  | 'blank';   // Deleted cell (blank bank space, no slot)

type PresetTemplateSize = 'desktopTiny' | 'desktopSmall' | 'desktop' | 'rack' | 'rackLong' | 'rackShort';
type CustomTemplateId = string; // Format: 'custom:template-id'
type TemplateSize = PresetTemplateSize | CustomTemplateId | 'none';

// Custom template definition
interface CustomTemplate {
  id: string;
  name: string;
  cols: number;
  rows: number;
  description?: string;
  createdAt: number;
  updatedAt: number;
  // Persisted port layout (single-face or multi-face)
  layout?: {
    numberingSchemeId?: string;
    simple?: {
      stage: { width: number; height: number };
      ports: any[];
      templateSize?: TemplateSize;
      blanks?: Array<{ row: number; col: number }>;
    };
    multiFace?: {
      faces: Record<string, {
        stage: { width: number; height: number };
        ports: any[];
        templateSize?: TemplateSize;
        blanks?: Array<{ row: number; col: number }>;
      }>;
      defaultFace?: EquipmentFace;
    };
  };
}

// Template definition interface
interface TemplateDefinition {
  name: string;
  cols: number;
  rows: number;
  description: string;
}

// Port type metadata for UI and rendering
const PORT_TYPES: Record<PortKind, { 
  label: string; 
  color: string; 
  icon?: any;
  description: string;
}> = {
  rj45: { label: 'RJ45', color: '#10b981', icon: EthernetPort, description: 'Ethernet port' },
  sfp: { label: 'SFP', color: '#3b82f6', icon: Cable, description: 'Fiber optic port' },
  sfpPlus: { label: 'SFP+', color: '#3b82f6', icon: Cable, description: 'SFP+ port (10Gbps fiber)' },
  ac: { label: 'AC', color: '#ef4444', icon: Power, description: 'AC power outlet' },
  dc: { label: 'DC', color: '#f97316', icon: Zap, description: 'DC power terminal' },
  pvPlus: { label: 'PV+', color: '#dc2626', icon: Zap, description: 'PV positive' },
  pvMinus: { label: 'PV−', color: '#111827', icon: Zap, description: 'PV negative' },
  batPlus: { label: 'BAT+', color: '#f43f5e', icon: Zap, description: 'Battery positive' },
  batMinus: { label: 'BAT−', color: '#1e293b', icon: Zap, description: 'Battery negative' },
  usb: { label: 'USB', color: '#8b5cf6', icon: Usb, description: 'USB port' },
  console: { label: 'Console', color: '#06b6d4', icon: Terminal, description: 'Console/Serial port' },
  hdmi: { label: 'HDMI', color: '#ec4899', icon: Monitor, description: 'Video output' },
  audio: { label: 'Audio', color: '#14b8a6', icon: Volume2, description: 'Audio jack' },
  coax: { label: 'Coax', color: '#a855f7', icon: Radio, description: 'Coax/RF connector' },
  custom: { label: 'Custom', color: '#64748b', icon: Box, description: 'Custom port type' },
  empty: { label: 'Empty', color: '#374151', icon: Circle, description: 'Empty cell' },
  blank: { label: 'Blank', color: 'transparent', icon: Square, description: 'Blank bank space (no port slot)' },
};

interface GridCell {
  row: number;
  col: number;
  type: PortKind;
  number?: number;
  speedsMbps?: number[];
  poeIn?: import('../../types/deviceModel').PoEInSpec;
  poeOut?: import('../../types/deviceModel').PoEOutSpec;
  poeInRoleId?: string;
  poeOutRoleId?: string;
  passthrough?: boolean;
  comboGroup?: string;
}

const NAMING_SCHEME_PREFIX_KINDS: Array<{ key: string; label: string }> = [
  { key: 'rj45', label: 'RJ45' },
  { key: 'sfp', label: 'SFP' },
  { key: 'sfpPlus', label: 'SFP+' },
];

function emptySchemePrefixes(): Record<string, string> {
  return { rj45: '', sfp: '', sfpPlus: '' };
}

function isVacantPortType(type: PortKind | string | undefined | null): boolean {
  return !type || type === 'empty' || type === 'blank';
}

function gridCellWithKind(cell: GridCell, type: PortKind): GridCell {
  if (isVacantPortType(type)) {
    return { row: cell.row, col: cell.col, type, number: undefined };
  }
  const stripped = applyPortKindChange(
    {
      id: 'tmp',
      number: cell.number || 1,
      kind: (cell.type as any) || 'rj45',
      x: 0,
      y: 0,
      poeIn: cell.poeIn,
      poeOut: cell.poeOut,
      poeInRoleId: cell.poeInRoleId,
      poeOutRoleId: cell.poeOutRoleId,
      speedsMbps: cell.speedsMbps,
      passthrough: cell.passthrough,
      comboGroup: cell.comboGroup,
    },
    String(type)
  );
  return {
    ...cell,
    type,
    poeIn: stripped.poeIn,
    poeOut: stripped.poeOut,
    poeInRoleId: stripped.poeInRoleId,
    poeOutRoleId: stripped.poeOutRoleId,
    speedsMbps: stripped.speedsMbps,
    passthrough: stripped.passthrough,
    comboGroup: stripped.comboGroup,
  };
}

function applyPortNumberToCells(cells: GridCell[], row: number, col: number, nextNumber: number): GridCell[] {
  const source = cells.find((c) => c.row === row && c.col === col);
  if (!source || isVacantPortType(source.type)) return cells;
  // Numbers are unique per port type — RJ45 #1 and SFP #1 may both exist
  const occupant = cells.find(
    (c) =>
      !isVacantPortType(c.type) &&
      c.type === source.type &&
      c.number === nextNumber &&
      !(c.row === row && c.col === col)
  );
  return cells.map((c) => {
    if (c.row === row && c.col === col) return { ...c, number: nextNumber };
    if (occupant && c.row === occupant.row && c.col === occupant.col) {
      return { ...c, number: source.number };
    }
    return c;
  });
}

interface SimpleLayoutEditorProps {
  onSave: (layout: any, equipmentData?: any, saveTarget?: 'instance' | 'library') => void;
  onClose: () => void;
  equipmentName?: string;
  equipmentManufacturer?: string;
  equipmentLabel?: string;
  initialLayout?: any;
  portCount: number;
  enableMultiFace?: boolean; // Enable multi-face editing mode
  standalone?: boolean; // If true, editor is in standalone mode (not tied to equipment)
  isCreatingNew?: boolean; // If true, we're creating a new equipment
  showDualSave?: boolean; // Canvas instance: Save (this instance only) + Save (Update Library)
  initialDeviceModel?: DeviceModel;
  equipmentType?: string;
  equipmentId?: string;
  enableElectrical?: boolean;
}

// Face metadata for UI
const FACE_INFO: Record<EquipmentFace, { label: string; description: string }> = {
  front: { label: 'Front', description: 'Front panel ports' },
  back: { label: 'Back', description: 'Rear panel ports' },
  left: { label: 'Left', description: 'Left side ports' },
  right: { label: 'Right', description: 'Right side ports' },
  top: { label: 'Top', description: 'Top panel ports' },
  bottom: { label: 'Bottom', description: 'Bottom panel ports' },
};

// Editor cell size (for visual grid) - larger for easier clicking
const EDITOR_CELL_SIZE = 32; // px
const EDITOR_CELL_GAP = 4;    // px
const EDITOR_VIEW_KEY = 'netprobuilder-layout-editor-view';
const INFO_PANEL_KEY = 'netprobuilder-layout-info-panel';

// Actual port rendering size (from portLayout.ts)
const ACTUAL_PORT_WIDTH = PORT.width;  // 18px
const ACTUAL_PORT_GAP = GRID;          // 6px

// Preset template definitions with different grid dimensions
const PRESET_TEMPLATES: Record<PresetTemplateSize, TemplateDefinition> = {
  desktopTiny: {
    name: 'Desktop Tiny',
    cols: 5,
    rows: 1,
    description: '5 ports',
  },
  desktopSmall: {
    name: 'Desktop Small',
    cols: 8,
    rows: 1,
    description: '8 ports',
  },
  desktop: {
    name: 'Desktop Large',
    cols: 12,
    rows: 2,
    description: '8-16 ports',
  },
  rack: {
    name: 'Rackmount',
    cols: 24,
    rows: 2,
    description: '24 port rack',
  },
  rackLong: {
    name: 'Rackmount Long Ears',
    cols: 24,
    rows: 2,
    description: '24 port rack',
  },
  rackShort: {
    name: 'Rackmount Short Ears',
    cols: 16,
    rows: 2,
    description: '16 port rack',
  },
};

// Template storage and management
const TEMPLATE_STORAGE_KEY = 'netprobuilder-custom-templates';

// Available icons for custom port types (excluding icons already used in built-in port types)
// Built-in port types use: Power, Zap, Usb, Terminal, Monitor, Volume2, Radio
const AVAILABLE_ICONS = [
  { name: 'Box', component: Box },
  { name: 'Layers', component: Layers },
  { name: 'Circle', component: Circle },
  { name: 'Cpu', component: Cpu },
  { name: 'HardDrive', component: HardDrive },
  { name: 'Network', component: Network },
  { name: 'Wifi', component: Wifi },
  { name: 'Cable', component: Cable },
  { name: 'Plug', component: Plug },
  { name: 'Battery', component: Battery },
  { name: 'Lightbulb', component: Lightbulb },
  { name: 'Settings', component: Settings },
  { name: 'Activity', component: Activity },
  { name: 'Database', component: Database },
  { name: 'Server', component: Server },
  { name: 'Cloud', component: Cloud },
  { name: 'Lock', component: Lock },
  { name: 'Unlock', component: Unlock },
  { name: 'Key', component: Key },
  { name: 'Shield', component: Shield },
  { name: 'Eye', component: Eye },
  { name: 'EyeOff', component: EyeOff },
  { name: 'Bell', component: Bell },
  { name: 'Star', component: Star },
  { name: 'Heart', component: Heart },
  { name: 'Flag', component: Flag },
  { name: 'Tag', component: Tag },
  { name: 'Bookmark', component: Bookmark },
  { name: 'Folder', component: Folder },
  { name: 'File', component: File },
  { name: 'Image', component: Image },
  { name: 'Video', component: Video },
  { name: 'Music', component: Music },
  { name: 'Mic', component: Mic },
  { name: 'Headphones', component: Headphones },
  { name: 'Gamepad2', component: Gamepad2 },
  { name: 'Tv', component: Tv },
  { name: 'Laptop', component: Laptop },
  { name: 'Smartphone', component: Smartphone },
  { name: 'Tablet', component: Tablet },
  { name: 'Watch', component: Watch },
  { name: 'Camera', component: Camera },
  { name: 'Printer', component: Printer },
  { name: 'Mail', component: Mail },
  { name: 'Phone', component: Phone },
  { name: 'MessageSquare', component: MessageSquare },
  { name: 'Send', component: Send },
  { name: 'Share2', component: Share2 },
  { name: 'Link', component: Link },
  { name: 'Unlink', component: Unlink },
  { name: 'Copy', component: Copy },
  { name: 'Scissors', component: Scissors },
  { name: 'FileCheck', component: FileCheck },
  { name: 'FileX', component: FileX },
  { name: 'FolderOpen', component: FolderOpen },
  { name: 'FolderPlus', component: FolderPlus },
  { name: 'Archive', component: Archive },
  { name: 'Package', component: Package },
  { name: 'ShoppingCart', component: ShoppingCart },
  { name: 'CreditCard', component: CreditCard },
  { name: 'DollarSign', component: DollarSign },
  { name: 'TrendingUp', component: TrendingUp },
  { name: 'BarChart', component: BarChart },
  { name: 'PieChart', component: PieChart },
  { name: 'LineChart', component: LineChart },
  { name: 'Calendar', component: Calendar },
  { name: 'Clock', component: Clock },
  { name: 'Timer', component: Timer },
  { name: 'BellRing', component: BellRing },
  { name: 'BellOff', component: BellOff },
  { name: 'VolumeX', component: VolumeX },
  { name: 'Volume1', component: Volume1 },
  { name: 'Bluetooth', component: Bluetooth },
  { name: 'WifiOff', component: WifiOff },
  { name: 'Signal', component: Signal },
  { name: 'SignalHigh', component: SignalHigh },
  { name: 'SignalLow', component: SignalLow },
  { name: 'SignalZero', component: SignalZero },
  { name: 'Router', component: Router },
];

const loadCustomTemplates = (): Record<string, CustomTemplate> => {
  try {
    const stored = localStorage.getItem(TEMPLATE_STORAGE_KEY);
    if (!stored) return {};
    const templates = JSON.parse(stored);
    return templates;
  } catch {
    return {};
  }
};

const saveCustomTemplates = (templates: Record<string, CustomTemplate>) => {
  try {
    localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Failed to save custom templates:', error);
  }
};

const createCustomTemplate = (name: string, cols: number, rows: number, description?: string): CustomTemplate => {
  const id = `custom:${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  return {
    id,
    name,
    cols,
    rows,
    description,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
};

const deleteCustomTemplate = (templateId: string) => {
  const templates = loadCustomTemplates();
  delete templates[templateId];
  saveCustomTemplates(templates);
};

// Migration function to map old template IDs to new ones
const migrateTemplateId = (oldTemplateId: string): PresetTemplateSize => {
  const migrationMap: Record<string, PresetTemplateSize> = {
    'tiny': 'desktopTiny',
    'small': 'desktopSmall',
    'medium': 'desktop',
    'rackTall': 'rack',
    'large': 'rack',
    'xlarge': 'rack',
  };
  
  return migrationMap[oldTemplateId] || 'desktop';
};

// Helper function to get template definition by ID
const needsTemplateMigration = (templateSize: string) =>
  !templateSize.startsWith('custom:')
  && !parseFittedGridId(templateSize)
  && !(templateSize in PRESET_TEMPLATES)
  && templateSize !== 'none';

const getTemplate = (templateSize: TemplateSize): TemplateDefinition => {
  // Handle 'none' case - return a minimal template
  if (templateSize === 'none') {
    return { name: 'No template selected', cols: 0, rows: 0, description: '' };
  }

  const fitted = parseFittedGridId(String(templateSize));
  if (fitted) {
    return {
      name: `Current layout (${fitted.cols}×${fitted.rows})`,
      cols: fitted.cols,
      rows: fitted.rows,
      description: 'Sized to saved ports',
    };
  }
  
  // Check if it's an old template ID that needs migration
  if (typeof templateSize === 'string' && needsTemplateMigration(templateSize)) {
    const migratedId = migrateTemplateId(templateSize);
    return PRESET_TEMPLATES[migratedId];
  }
  
  // Check if it's a preset template
  if (templateSize in PRESET_TEMPLATES) {
    return PRESET_TEMPLATES[templateSize as PresetTemplateSize];
  }
  
  // Check if it's a custom template
  if (templateSize.startsWith('custom:')) {
    const templates = loadCustomTemplates();
    const template = templates[templateSize];
    if (template) {
      return {
        name: template.name,
        cols: template.cols,
        rows: template.rows,
        description: template.description || `${template.cols}×${template.rows} custom`,
      };
    }
  }
  
  // Fallback to desktop
  return PRESET_TEMPLATES.desktop;
};

const SimpleLayoutEditor: React.FC<SimpleLayoutEditorProps> = ({
  onSave,
  onClose,
  equipmentName,
  equipmentManufacturer,
  equipmentLabel,
  initialLayout,
  portCount,
  enableMultiFace = false,
  standalone = false,
  isCreatingNew = false,
  showDualSave = false,
  initialDeviceModel,
  equipmentType,
  equipmentId,
  enableElectrical = false,
}) => {
  // Multi-face state - enabled by default for new system
  const [isMultiFaceMode, setIsMultiFaceMode] = useState<boolean>(enableMultiFace || !!initialLayout?.multiFace || true);
  const [activeFace, setActiveFace] = useState<EquipmentFace>('front');
  const showElectrical = enableElectrical && !standalone;
  const [editorView, setEditorView] = useState<'classic' | 'blueprint'>(() => {
    try {
      return sessionStorage.getItem(EDITOR_VIEW_KEY) === 'blueprint' ? 'blueprint' : 'classic';
    } catch {
      return 'classic';
    }
  });
  const [blueprintCardId, setBlueprintCardId] = useState<string | null>(null);
  const [presetMenu, setPresetMenu] = useState<'input' | 'output' | 'role' | null>(null);
  const [showLayoutPanel, setShowLayoutPanel] = useState(true);
  const [showInfoPanel, setShowInfoPanel] = useState(() => {
    try {
      return sessionStorage.getItem(INFO_PANEL_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const toggleInfoPanel = () => {
    setShowInfoPanel((v) => {
      const next = !v;
      try {
        sessionStorage.setItem(INFO_PANEL_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const [cellTooltip, setCellTooltip] = useState<{ title: string; lines: string[]; x: number; y: number } | null>(null);
  const [hoverPortId, setHoverPortId] = useState<string | null>(null);
  const [isPortPlaceOpen, setIsPortPlaceOpen] = useState(false);
  const portPlaceBtnRef = useRef<HTMLButtonElement>(null);
  const portPlaceMenuRef = useRef<HTMLDivElement>(null);
  const [portPlaceMenuPos, setPortPlaceMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [deviceModelDraft, setDeviceModelDraft] = useState<DeviceModel | null>(() => {
    if (!enableElectrical || standalone) return null;
    if (initialDeviceModel) return initElectricalDraft(JSON.parse(JSON.stringify(initialDeviceModel)));
    return null;
  });
  const initialDeviceModelJsonRef = useRef(
    enableElectrical && !standalone && initialDeviceModel
      ? JSON.stringify(initElectricalDraft(JSON.parse(JSON.stringify(initialDeviceModel))))
      : ''
  );
  
  // Selected port type for placement (paint tool style)
  const [selectedPortType, setSelectedPortType] = useState<PortKind | 'empty'>('rj45');
  // Drag mode state
  const [isDragMode, setIsDragMode] = useState(false);
  const [isNumberMode, setIsNumberMode] = useState(false);
  const [peekPortTypes, setPeekPortTypes] = useState(false);
  const [activeNumberCell, setActiveNumberCell] = useState<{ row: number; col: number } | null>(null);
  const [numberDraft, setNumberDraft] = useState('');
  const [customNamingSchemes, setCustomNamingSchemes] = useState<Record<string, PortNamingScheme>>(() => loadCustomPortNamingSchemes());
  const [numberingSchemeId, setNumberingSchemeId] = useState<string>(
    () => initialLayout?.numberingSchemeId || NUMBERS_ONLY_SCHEME_ID
  );
  const [isSchemeManagerOpen, setIsSchemeManagerOpen] = useState(false);
  const [editingSchemeId, setEditingSchemeId] = useState<string | null>(null);
  const [newSchemeName, setNewSchemeName] = useState('');
  const [newSchemePrefixes, setNewSchemePrefixes] = useState<Record<string, string>>(emptySchemePrefixes);
  const initialNumberingSchemeIdRef = useRef(initialLayout?.numberingSchemeId || NUMBERS_ONLY_SCHEME_ID);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [paintedCells, setPaintedCells] = useState<Set<string>>(new Set());
  const [dragStartCell, setDragStartCell] = useState<{ row: number; col: number } | null>(null);
  const [faceGrids, setFaceGrids] = useState<Record<EquipmentFace, GridCell[]>>({
    front: [],
    back: [],
    left: [],
    right: [],
    top: [],
    bottom: [],
  });
  // Determine initial template based on saved layout or port count
  const getDefaultTemplate = (): TemplateSize => {
    return templateIdFromSimpleLayout(initialLayout?.simple, portCount) as TemplateSize;
  };

  const defaultTemplate = getDefaultTemplate();

  // Per-face template sizes (for multi-face mode)
  const [faceTemplateSizes, setFaceTemplateSizes] = useState<Record<EquipmentFace, TemplateSize>>({
    front: 'none',
    back: 'none',
    left: 'none',
    right: 'none',
    top: 'none',
    bottom: 'none',
  });

  // Single template size (for single-face mode, backward compatibility)
  const [templateSize, setTemplateSize] = useState<TemplateSize>(defaultTemplate);

  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState<Record<string, CustomTemplate>>(loadCustomTemplates());
  const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [isSavingCurrentAsTemplate, setIsSavingCurrentAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateCols, setNewTemplateCols] = useState(12);
  const [newTemplateRows, setNewTemplateRows] = useState(2);
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  
  // Custom port types state
  const [customPortTypes, setCustomPortTypes] = useState<Record<string, CustomPortType>>(loadCustomPortTypes());
  const [isCustomPortDropdownOpen, setIsCustomPortDropdownOpen] = useState(false);
  const [isCreatingCustomPort, setIsCreatingCustomPort] = useState(false);
  const [newCustomPortName, setNewCustomPortName] = useState('');
  const [newCustomPortIcon, setNewCustomPortIcon] = useState('Box');
  const [newCustomPortColor, setNewCustomPortColor] = useState('#64748b');
  const customPortDropdownRef = useRef<HTMLDivElement>(null);
  const customPortMenuRef = useRef<HTMLDivElement>(null);
  const customPortButtonRef = useRef<HTMLButtonElement>(null);
  const [customPortMenuPosition, setCustomPortMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [showDeleteCustomPortConfirm, setShowDeleteCustomPortConfirm] = useState(false);
  const [portTypeToDelete, setPortTypeToDelete] = useState<string | null>(null);
  
  // Equipment creation state (for creating new equipment)
  const [newEquipmentName, setNewEquipmentName] = useState<string>(equipmentName || '');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>(equipmentManufacturer || '');
  const [isCreatingManufacturer, setIsCreatingManufacturer] = useState(false);
  const [newManufacturerName, setNewManufacturerName] = useState('');
  const [isManufacturerDropdownOpen, setIsManufacturerDropdownOpen] = useState(false);
  const manufacturerDropdownRef = useRef<HTMLDivElement>(null);
  
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  
  const [equipmentPrice, setEquipmentPrice] = useState<string>('');
  const [selectedIcon, setSelectedIcon] = useState<string>('Package');
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);
  const iconDropdownRef = useRef<HTMLDivElement>(null);
  
  // Get unique manufacturers from equipment database
  const availableManufacturers = useMemo(() => {
    const manufacturers = new Set<string>();
    equipmentDatabase.forEach((eq: any) => {
      if (eq.manufacturer) {
        manufacturers.add(eq.manufacturer.toLowerCase());
      }
    });
    return Array.from(manufacturers).sort();
  }, []);
  
  // Get unique categories/types from equipment database
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    equipmentDatabase.forEach((eq: any) => {
      if (eq.type) {
        categories.add(eq.type.toLowerCase());
      }
    });
    return Array.from(categories).sort();
  }, []);
  
  // Available icons for equipment
  const availableIcons = [
    { name: 'Package', component: Package },
    { name: 'Router', component: Router },
    { name: 'Switch', component: EthernetPort },
    { name: 'Wifi', component: Wifi },
    { name: 'Server', component: Server },
    { name: 'Zap', component: Zap },
    { name: 'HardDrive', component: HardDrive },
    { name: 'Box', component: Box },
    { name: 'Network', component: Network },
    { name: 'Cable', component: Cable },
    { name: 'Plug', component: Plug },
    { name: 'Database', component: Database },
    { name: 'Cloud', component: Cloud },
    { name: 'Activity', component: Activity },
    { name: 'Settings', component: Settings },
  ];
  
  // Add New Template menu state
  const [isAddNewTemplateMenuOpen, setIsAddNewTemplateMenuOpen] = useState(false);
  const [addNewTemplateMenuPosition, setAddNewTemplateMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const addNewTemplateMenuRef = useRef<HTMLDivElement>(null);
  const templateSelectRef = useRef<HTMLSelectElement>(null);
  const addNewTemplateButtonRef = useRef<HTMLButtonElement>(null);
  
  // Standalone editor state
  const [isStandaloneEditorOpen, setIsStandaloneEditorOpen] = useState(false);
  const isOpeningStandalone = useRef(false);

  // Import/Export state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  
  // Save confirmation state
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [pendingSaveTarget, setPendingSaveTarget] = useState<'instance' | 'library' | undefined>(undefined);
  const [exportMetadata, setExportMetadata] = useState({
    name: equipmentName || 'Equipment Layout',
    description: '',
    manufacturer: '',
    model: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current template based on mode
  const currentTemplateSize = isMultiFaceMode ? faceTemplateSizes[activeFace] : templateSize;
  const template = getTemplate(currentTemplateSize);
  const totalCells = template.cols * template.rows;
  
  // Reload custom templates when they change
  useEffect(() => {
    setCustomTemplates(loadCustomTemplates());
  }, [isTemplateManagerOpen]);

  // Calculate stage dimensions for EDITOR (visual grid, larger for clicking)
  const editorStageWidth = template.cols > 0 ? template.cols * (EDITOR_CELL_SIZE + EDITOR_CELL_GAP) - EDITOR_CELL_GAP + 36 : 200; // +36 for padding, min 200px
  const editorStageHeight = template.rows > 0 ? template.rows * (EDITOR_CELL_SIZE + EDITOR_CELL_GAP) - EDITOR_CELL_GAP + 20 : 200; // +20 for padding, min 200px
  
  // Calculate stage dimensions for ACTUAL RENDERING (saved layout, tighter spacing)
  const actualStageWidth = template.cols * (ACTUAL_PORT_WIDTH + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP + 36;
  // Add more vertical padding for the stage background (top/bottom margins)
  const actualStageHeight = template.rows * (PORT.height + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP + 44;

  // Helper function to create empty grid
  const createEmptyGrid = (rows: number, cols: number): GridCell[] => {
    const cells: GridCell[] = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        cells.push({ row, col, type: 'empty' });
      }
    }
    return cells;
  };

  // Helper function to load grid from saved layout
  const loadGridFromLayout = (savedPorts: any[], rows: number, cols: number, stageHeight: number, blanks?: Array<{ row: number; col: number }>): GridCell[] => {
    const cells = createEmptyGrid(rows, cols);
    const padding = 18;
    const startY = Math.round((stageHeight - (rows * (PORT.height + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP)) / 2);
    
    savedPorts.forEach((port: any) => {
      const col = Math.round((port.x - padding) / (ACTUAL_PORT_WIDTH + ACTUAL_PORT_GAP));
      const row = Math.round((port.y - startY) / (PORT.height + ACTUAL_PORT_GAP));
      
      const cellIndex = cells.findIndex(c => c.row === row && c.col === col);
      if (cellIndex !== -1 && row >= 0 && row < rows && col >= 0 && col < cols) {
        cells[cellIndex].type = port.kind || 'rj45';
        if (typeof port.number === 'number' && port.number >= 1) {
          cells[cellIndex].number = port.number;
        }
        cells[cellIndex].speedsMbps = port.speedsMbps;
        cells[cellIndex].poeIn = port.poeIn;
        cells[cellIndex].poeOut = port.poeOut;
        cells[cellIndex].poeInRoleId = port.poeInRoleId;
        cells[cellIndex].poeOutRoleId = port.poeOutRoleId;
        cells[cellIndex].passthrough = port.passthrough;
        cells[cellIndex].comboGroup = port.comboGroup;
      }
    });

    (blanks || []).forEach((blank) => {
      const cellIndex = cells.findIndex((c) => c.row === blank.row && c.col === blank.col);
      if (cellIndex !== -1 && isVacantPortType(cells[cellIndex].type)) {
        cells[cellIndex].type = 'blank';
        cells[cellIndex].number = undefined;
      }
    });
    
    return fillMissingPortNumbers(cells);
  };

  const cellsFromSavedLayout = (layout: {
    ports?: any[];
    blanks?: Array<{ row: number; col: number }>;
    stage?: { width: number; height: number };
    templateSize?: string;
  }): GridCell[] => {
    const inferred = inferLayoutGrid(layout as any);
    if (!inferred.cols || !inferred.rows) {
      return [];
    }
    return fillMissingPortNumbers(
      inferred.cells.map((cell) => {
        const port = cell.port;
        if (cell.blank) {
          return { row: cell.row, col: cell.col, type: 'blank' as PortKind };
        }
        if (!port) {
          return { row: cell.row, col: cell.col, type: 'empty' as PortKind };
        }
        return {
          row: cell.row,
          col: cell.col,
          type: (port.kind || 'rj45') as PortKind,
          number: typeof port.number === 'number' ? port.number : undefined,
          speedsMbps: port.speedsMbps,
          poeIn: port.poeIn,
          poeOut: port.poeOut,
          poeInRoleId: (port as any).poeInRoleId,
          poeOutRoleId: (port as any).poeOutRoleId,
          passthrough: port.passthrough,
          comboGroup: (port as any).comboGroup,
        };
      })
    );
  };

  // Initialize grid state (for single-face mode)
  const [grid, setGrid] = useState<GridCell[]>(() => {
    // Try to populate from existing saved layout
    if (initialLayout?.simple?.ports && Array.isArray(initialLayout.simple.ports)) {
      console.log('Loading existing single-face layout from save');
      return cellsFromSavedLayout(initialLayout.simple);
    }
    
    // No saved layout - create grid with first N cells as RJ45 by default
    const cells = createEmptyGrid(template.rows, template.cols);
    cells.forEach((cell, idx) => {
      if (idx < portCount) {
        cell.type = 'rj45';
      }
    });
    
    return fillMissingPortNumbers(cells);
  });

  // Initialize multi-face grids from saved layout
  useEffect(() => {
    const simplePorts = initialLayout?.simple?.ports;
    const multiFaces = initialLayout?.multiFace?.faces;
    const multiHasPorts = !!(
      multiFaces &&
      Object.values(multiFaces).some(
        (layout: any) => Array.isArray(layout?.ports) && layout.ports.length > 0
      )
    );

    if (isMultiFaceMode && multiFaces && multiHasPorts) {
      console.log('Loading multi-face layout from save');
      const newFaceGrids: Record<EquipmentFace, GridCell[]> = {
        front: [],
        back: [],
        left: [],
        right: [],
        top: [],
        bottom: [],
      };
      const newFaceTemplateSizes: Record<EquipmentFace, TemplateSize> = {
        front: 'none',
        back: 'none',
        left: 'none',
        right: 'none',
        top: 'none',
        bottom: 'none',
      };
      
      // Load each face's ports and template size
      Object.entries(multiFaces).forEach(([face, layout]: [string, any]) => {
        const faceKey = face as EquipmentFace;
        
        // Check if face has been populated (has ports or template)
        const hasPorts = layout?.ports && Array.isArray(layout.ports) && layout.ports.length > 0;
        const hasTemplate = layout?.templateSize;
        
        if (hasPorts || hasTemplate) {
          let faceTemplateSize = templateIdFromSimpleLayout(layout, portCount) as TemplateSize;
          if (typeof faceTemplateSize === 'string' && needsTemplateMigration(faceTemplateSize)) {
            faceTemplateSize = migrateTemplateId(faceTemplateSize);
          }
        newFaceTemplateSizes[faceKey] = faceTemplateSize as TemplateSize;
        
          if (hasPorts) {
          newFaceGrids[faceKey] = cellsFromSavedLayout(layout);
        } else {
            // Has template but no ports, create empty grid
          const faceTemplate = getTemplate(faceTemplateSize as TemplateSize);
          newFaceGrids[faceKey] = createEmptyGrid(faceTemplate.rows, faceTemplate.cols);
        }
        }
        // If face hasn't been populated, keep template size as 'none' and grid as empty array
      });
      
      setFaceGrids(newFaceGrids);
      setFaceTemplateSizes(newFaceTemplateSizes);
      
      // Set default face
      if (initialLayout.multiFace.defaultFace) {
        setActiveFace(initialLayout.multiFace.defaultFace);
      }
    } else if (isMultiFaceMode && Array.isArray(simplePorts) && simplePorts.length > 0) {
      // Catalog / older saves are simple-only (or multiFace was emptied); seed front.
      let faceTemplateSize = templateIdFromSimpleLayout(initialLayout.simple, portCount) as TemplateSize;
      if (typeof faceTemplateSize === 'string' && needsTemplateMigration(faceTemplateSize)) {
        faceTemplateSize = migrateTemplateId(faceTemplateSize);
      }
      setFaceTemplateSizes({
        front: faceTemplateSize,
        back: 'none',
        left: 'none',
        right: 'none',
        top: 'none',
        bottom: 'none',
      });
      setFaceGrids({
        front: cellsFromSavedLayout(initialLayout.simple),
        back: [],
        left: [],
        right: [],
        top: [],
        bottom: [],
      });
      setActiveFace('front');
    } else if (isMultiFaceMode) {
      // Initialize empty multi-face grids with 'none' template
      setFaceTemplateSizes({
        front: 'none',
        back: 'none',
        left: 'none',
        right: 'none',
        top: 'none',
        bottom: 'none',
      });
      setFaceGrids({
        front: [],
        back: [],
        left: [],
        right: [],
        top: [],
        bottom: [],
      });
    }
  }, [isMultiFaceMode, defaultTemplate]); // Run when multi-face mode changes

  // Store initial state for change detection
  const initialGridRef = useRef<GridCell[] | null>(null);
  const initialFaceGridsRef = useRef<Record<EquipmentFace, GridCell[]> | null>(null);
  const initialTemplateSizeRef = useRef<TemplateSize | null>(null);
  const initialFaceTemplateSizesRef = useRef<Record<EquipmentFace, TemplateSize> | null>(null);
  const hasInitializedRef = useRef(false);
  const electricalBaselineReadyRef = useRef(false);
  const blueprintGraphCommitRef = useRef<(() => DeviceModel) | null>(null);
  const faceGridsLiveRef = useRef(faceGrids);
  const gridLiveRef = useRef(grid);
  const faceTemplateSizesLiveRef = useRef(faceTemplateSizes);
  const templateSizeLiveRef = useRef(templateSize);
  faceGridsLiveRef.current = faceGrids;
  gridLiveRef.current = grid;
  faceTemplateSizesLiveRef.current = faceTemplateSizes;
  templateSizeLiveRef.current = templateSize;

  // Reset and capture initial state when modal opens (after catalog overlay)
  useEffect(() => {
    hasInitializedRef.current = false;
    electricalBaselineReadyRef.current = false;
    initialGridRef.current = null;
    initialFaceGridsRef.current = null;
    initialTemplateSizeRef.current = null;
    initialFaceTemplateSizesRef.current = null;

    const timer = setTimeout(() => {
      if (hasInitializedRef.current) return;
      if (isMultiFaceMode) {
        initialFaceGridsRef.current = JSON.parse(JSON.stringify(faceGridsLiveRef.current));
        initialFaceTemplateSizesRef.current = JSON.parse(JSON.stringify(faceTemplateSizesLiveRef.current));
      } else {
        initialGridRef.current = JSON.parse(JSON.stringify(gridLiveRef.current));
        initialTemplateSizeRef.current = templateSizeLiveRef.current;
      }
      hasInitializedRef.current = true;
    }, 550);

    return () => clearTimeout(timer);
  }, []); // Only run once when component mounts

  // Helper function to deep compare grids
  const gridsAreEqual = (grid1: GridCell[], grid2: GridCell[]): boolean => {
    if (grid1.length !== grid2.length) return false;
    return grid1.every((cell, idx) => {
      const otherCell = grid2[idx];
      return otherCell && 
             cell.row === otherCell.row && 
             cell.col === otherCell.col && 
             cell.type === otherCell.type &&
             cell.number === otherCell.number &&
             cell.poeInRoleId === otherCell.poeInRoleId &&
             cell.poeOutRoleId === otherCell.poeOutRoleId &&
             !!cell.poeIn === !!otherCell.poeIn &&
             !!cell.poeOut === !!otherCell.poeOut &&
             !!cell.passthrough === !!otherCell.passthrough;
    });
  };

  // Check if there are layout changes
  const layoutHasChanges = useMemo(() => {
    if (!hasInitializedRef.current) {
      return false; // Still initializing
    }

    if (numberingSchemeId !== initialNumberingSchemeIdRef.current) {
      return true;
    }

    if (isMultiFaceMode) {
      if (initialFaceGridsRef.current === null || initialFaceTemplateSizesRef.current === null) {
        return false; // Still initializing
      }
      
      // Check if template sizes changed
      const templateSizesChanged = Object.keys(faceTemplateSizes).some(
        (face) => faceTemplateSizes[face as EquipmentFace] !== initialFaceTemplateSizesRef.current![face as EquipmentFace]
      );
      
      if (templateSizesChanged) {
        return true;
      }
      
      // Check if grids changed
      const gridsChanged = Object.keys(faceGrids).some((face) => {
        const currentGrid = faceGrids[face as EquipmentFace];
        const initialGrid = initialFaceGridsRef.current![face as EquipmentFace] || [];
        
        return !gridsAreEqual(currentGrid, initialGrid);
      });
      
      return gridsChanged;
    } else {
      if (initialGridRef.current === null || initialTemplateSizeRef.current === null) {
        return false; // Still initializing
      }
      
      // Check if template size changed
      if (templateSize !== initialTemplateSizeRef.current) {
        return true;
      }
      
      // Check if grid changed using deep comparison
      return !gridsAreEqual(grid, initialGridRef.current);
    }
  }, [isMultiFaceMode, grid, faceGrids, templateSize, faceTemplateSizes, numberingSchemeId]);

  const electricalHasChanges = !!showElectrical && !!deviceModelDraft
    && JSON.stringify(deviceModelDraft) !== initialDeviceModelJsonRef.current;
  const hasChanges = layoutHasChanges || electricalHasChanges;

  // Place selected port type on cell (works for both click and drag)
  const handleCellPlace = (row: number, col: number, skipIfPainted = false) => {
    const cellKey = `${row}-${col}`;
    
    // If in drag mode and we're tracking painted cells, check if already painted
    if (skipIfPainted && paintedCells.has(cellKey)) {
      return;
    }

    const paintCell = (cells: GridCell[]): GridCell[] => {
      const newType: PortKind = selectedPortType;
      return cells.map((cell) => {
        if (cell.row !== row || cell.col !== col) return cell;
        if (skipIfPainted) {
          setPaintedCells((prev) => new Set(prev).add(cellKey));
        }
        if (isVacantPortType(newType)) {
          return { row: cell.row, col: cell.col, type: newType, number: undefined };
        }
        if (!isVacantPortType(cell.type) && typeof cell.number === 'number') {
          const numberTaken = cells.some(
            (c) =>
              c.type === newType &&
              c.number === cell.number &&
              !(c.row === row && c.col === col)
          );
          if (!numberTaken) {
            return gridCellWithKind({ ...cell }, newType);
          }
          return gridCellWithKind(
            {
              ...cell,
              number: nextUnusedPortNumber(
                cells
                  .filter((c) => c.type === newType && !(c.row === row && c.col === col))
                  .map((c) => c.number)
              ),
            },
            newType
          );
        }
        return gridCellWithKind(
          {
            ...cell,
            number: nextUnusedPortNumber(
              cells
                .filter((c) => c.type === newType && !(c.row === row && c.col === col))
                .map((c) => c.number)
            ),
          },
          newType
        );
      });
    };
    
    if (isMultiFaceMode) {
      setFaceGrids(prev => ({
        ...prev,
        [activeFace]: paintCell(prev[activeFace]),
      }));
    } else {
      setGrid(prev => paintCell(prev));
    }
  };

  // Handle cell click (single placement)
  const handleCellClick = (row: number, col: number) => {
    if (isNumberMode) {
      return;
    }
    if (!isDragMode) {
      handleCellPlace(row, col, false);
    } else {
      // In drag mode, clicking starts a drag
      setIsDragging(true);
      setDragStartCell({ row, col });
      setPaintedCells(new Set());
      handleCellPlace(row, col, false);
    }
  };

  // Handle mouse down on cell (for drag mode)
  const handleCellMouseDown = (row: number, col: number) => {
    if (isNumberMode) return;
    if (isDragMode) {
      setIsDragging(true);
      setDragStartCell({ row, col });
      setPaintedCells(new Set());
      handleCellPlace(row, col, false);
    }
  };

  // Handle mouse move over cells (for drag mode)
  const handleCellMouseMove = (row: number, col: number) => {
    if (isDragMode && isDragging) {
      handleCellPlace(row, col, true); // Skip if already painted in this drag
    }
  };

  // Handle mouse up (end drag)
  const handleMouseUp = () => {
    if (isDragMode) {
      setIsDragging(false);
      setPaintedCells(new Set());
      setDragStartCell(null);
    }
  };

  // Add global mouse up handler to stop dragging when mouse leaves the grid
  useEffect(() => {
    if (isDragging && isDragMode) {
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setPaintedCells(new Set());
        setDragStartCell(null);
      };
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, isDragMode]);

  // Quick fill buttons
  const fillAllRJ45 = () => {
    setGrid(prev => fillMissingPortNumbers(prev.map((cell, idx) => ({
      ...cell,
      type: idx < portCount ? 'rj45' : 'empty',
      number: idx < portCount ? cell.number : undefined,
    }))));
  };

  const fillMixed = (sfpCount: number) => {
    setGrid(prev => fillMissingPortNumbers(prev.map((cell, idx) => ({
      ...cell,
      type: idx < sfpCount ? 'sfp' : 
            idx < portCount ? 'rj45' : 
            'empty',
      number: idx < portCount ? cell.number : undefined,
    }))));
  };

  const clearAll = () => {
    if (isMultiFaceMode) {
      // Clear all faces
      const emptyGrids: Record<EquipmentFace, GridCell[]> = {
        front: [],
        back: [],
        left: [],
        right: [],
        top: [],
        bottom: [],
      };
      Object.keys(FACE_INFO).forEach((face) => {
        const faceKey = face as EquipmentFace;
        const template = getTemplate(faceTemplateSizes[faceKey]);
        emptyGrids[faceKey] = createEmptyGrid(template.rows, template.cols);
      });
      setFaceGrids(emptyGrids);
    } else {
      // Single-face mode
      setGrid(prev => prev.map(cell => ({ ...cell, type: 'empty', number: undefined })));
    }
  };

  const clearFace = () => {
    if (!isMultiFaceMode) return; // Only works in multi-face mode
    
    // Clear only the active face
    const template = getTemplate(faceTemplateSizes[activeFace]);
    const emptyGrid = createEmptyGrid(template.rows, template.cols);
    
    setFaceGrids(prev => ({
      ...prev,
      [activeFace]: emptyGrid,
    }));
  };

  // Get current grid (either single-face or active face)
  const currentGrid = isMultiFaceMode ? faceGrids[activeFace] : grid;
  const numberingScheme = useMemo(
    () => getPortNamingScheme(numberingSchemeId, customNamingSchemes),
    [numberingSchemeId, customNamingSchemes]
  );
  const namingSchemes = useMemo(
    () => allPortNamingSchemes(customNamingSchemes),
    [customNamingSchemes]
  );

  const updateCurrentGrid = useCallback((updater: (cells: GridCell[]) => GridCell[]) => {
    if (isMultiFaceMode) {
      setFaceGrids((prev) => ({ ...prev, [activeFace]: updater(prev[activeFace]) }));
    } else {
      setGrid((prev) => updater(prev));
    }
  }, [isMultiFaceMode, activeFace]);

  const filledPortsInOrder = useCallback((cells: GridCell[]) => {
    return cells
      .filter((c) => !isVacantPortType(c.type))
      .sort((a, b) => a.row - b.row || a.col - b.col);
  }, []);

  const activateNumberCell = useCallback((row: number, col: number, cells: GridCell[]) => {
    const cell = cells.find((c) => c.row === row && c.col === col);
    if (!cell || isVacantPortType(cell.type)) return;
    setActiveNumberCell({ row, col });
    setNumberDraft(typeof cell.number === 'number' ? String(cell.number) : '');
  }, []);

  const moveActiveNumberCell = useCallback((dir: 1 | -1, cells: GridCell[], from = activeNumberCell) => {
    if (!from) return;
    const filled = filledPortsInOrder(cells);
    const idx = filled.findIndex((c) => c.row === from.row && c.col === from.col);
    if (idx < 0 || filled.length === 0) return;
    const next = filled[(idx + dir + filled.length) % filled.length];
    activateNumberCell(next.row, next.col, cells);
  }, [activeNumberCell, activateNumberCell, filledPortsInOrder]);

  const commitNumberDraft = useCallback((thenMove?: 1 | -1 | 0): GridCell[] => {
    if (!activeNumberCell) {
      return isMultiFaceMode ? faceGrids[activeFace] : grid;
    }
    const cells = isMultiFaceMode ? faceGrids[activeFace] : grid;
    const parsed = parseInt(numberDraft, 10);
    if (!numberDraft.trim() || !Number.isFinite(parsed) || parsed < 1 || parsed > 999) {
      const cell = cells.find((c) => c.row === activeNumberCell.row && c.col === activeNumberCell.col);
      setNumberDraft(typeof cell?.number === 'number' ? String(cell.number) : '');
      if (thenMove === 1 || thenMove === -1) moveActiveNumberCell(thenMove, cells);
      return cells;
    }
    const nextCells = applyPortNumberToCells(cells, activeNumberCell.row, activeNumberCell.col, parsed);
    updateCurrentGrid(() => nextCells);
    if (thenMove === 1 || thenMove === -1) {
      moveActiveNumberCell(thenMove, nextCells);
    } else {
      setNumberDraft(String(parsed));
    }
    return nextCells;
  }, [activeNumberCell, faceGrids, activeFace, grid, isMultiFaceMode, moveActiveNumberCell, numberDraft, updateCurrentGrid]);

  useEffect(() => {
    if (!isNumberMode) {
      setPeekPortTypes(false);
      setActiveNumberCell(null);
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (isSchemeManagerOpen) return;
      const target = e.target as HTMLElement | null;
      const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
      const inPortInput = target === numberInputRef.current;
      if ((e.key === 'p' || e.key === 'P') && !e.repeat && (!inField || inPortInput)) {
        e.preventDefault();
        setPeekPortTypes(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'p' || e.key === 'P') setPeekPortTypes(false);
    };
    const onBlur = () => setPeekPortTypes(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [isNumberMode, isSchemeManagerOpen]);

  useEffect(() => {
    if (isNumberMode && activeNumberCell && numberInputRef.current) {
      numberInputRef.current.focus();
      numberInputRef.current.select();
    }
  }, [isNumberMode, activeNumberCell]);

  // Calculate statistics
  const stats = useMemo(() => {
    const counts: Record<PortKind, number> = {
      rj45: 0, sfp: 0, sfpPlus: 0, ac: 0, dc: 0, pvPlus: 0, pvMinus: 0, batPlus: 0, batMinus: 0, usb: 0, console: 0, 
      hdmi: 0, audio: 0, coax: 0, custom: 0, empty: 0, blank: 0
    };
    
    currentGrid.forEach(cell => {
      counts[cell.type]++;
    });
    
    const totalPorts = currentGrid.filter(c => !isVacantPortType(c.type)).length;
    
    return { ...counts, totalPorts };
  }, [currentGrid]);

  // Helper function to convert grid to port layout
  const gridToPortLayout = (gridCells: GridCell[], templateSizeForLayout: TemplateSize) => {
    const ports: any[] = [];
    const numbered = fillMissingPortNumbers(gridCells);
    const scheme = getPortNamingScheme(numberingSchemeId, customNamingSchemes);
    
    // Calculate dimensions based on the provided template size
    const layoutTemplate = getTemplate(templateSizeForLayout);
    const padding = 18;
    const layoutStageWidth = layoutTemplate.cols * (ACTUAL_PORT_WIDTH + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP + 36;
    const layoutStageHeight = layoutTemplate.rows * (PORT.height + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP + 44;
    const startY = Math.round((layoutStageHeight - (layoutTemplate.rows * (PORT.height + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP)) / 2);

    numbered.forEach((cell) => {
      if (!isVacantPortType(cell.type) && typeof cell.number === 'number') {
        // Use actual port spacing for rendering (tighter)
        const x = padding + cell.col * (ACTUAL_PORT_WIDTH + ACTUAL_PORT_GAP);
        const y = startY + cell.row * (PORT.height + ACTUAL_PORT_GAP);
        const prefix = prefixForPortKind(scheme, String(cell.type));
        const label = formatPortLabel(scheme, String(cell.type), cell.number);
        
        ports.push({
          id: `p-${String(cell.type).replace(/[^a-zA-Z0-9_-]/g, '_')}-${cell.number}`,
          number: cell.number,
          ...(prefix ? { label } : {}),
          kind: cell.type,
          x,
          y,
          flipped: cell.row > 0,
          ...(cell.speedsMbps ? { speedsMbps: cell.speedsMbps } : {}),
          ...(cell.poeIn ? { poeIn: cell.poeIn } : {}),
          ...(cell.poeOut ? { poeOut: cell.poeOut } : {}),
          ...(cell.poeInRoleId ? { poeInRoleId: cell.poeInRoleId } : {}),
          ...(cell.poeOutRoleId ? { poeOutRoleId: cell.poeOutRoleId } : {}),
          ...(cell.passthrough ? { passthrough: true } : {}),
          ...(cell.comboGroup ? { comboGroup: cell.comboGroup } : {}),
        });
      }
    });

    return {
      stage: { width: layoutStageWidth, height: layoutStageHeight },
      ports,
      templateSize: templateSizeForLayout, // Save the template size for later restoration
      blanks: numbered
        .filter((cell) => cell.type === 'blank')
        .map((cell) => ({ row: cell.row, col: cell.col })),
    };
  };

  const withNumberingScheme = (layout: any) => ({ ...layout, numberingSchemeId });

  // Save layout using ACTUAL PORT DIMENSIONS (not editor dimensions)
  const handleSave = (saveTarget?: 'instance' | 'library') => {
    if (showElectrical && deviceModelDraft && !validateDeviceModel(deviceModelDraft).ok) {
      return;
    }
    // In standalone mode, save into template store (update or create)
    if (standalone) {
      // Build current layout from grids
      const currentLayout = withNumberingScheme(isMultiFaceMode ? {
        multiFace: {
          faces: Object.fromEntries(
            Object.entries(faceGrids).map(([face, gridCells]) => {
              const hasAnyPorts = gridCells.some(cell => cell.type !== 'empty');
              if (hasAnyPorts) {
                const faceKey = face as EquipmentFace;
                const faceTemplateSize = faceTemplateSizes[faceKey];
                return [face, gridToPortLayout(gridCells, faceTemplateSize)];
              }
              return [face, gridToPortLayout(gridCells, faceTemplateSizes[face as EquipmentFace])];
            })
          ),
          defaultFace: activeFace,
        },
      } : {
        simple: gridToPortLayout(grid, templateSize),
      });
      
      const templates = loadCustomTemplates();
      const isEditingExisting = typeof currentTemplateSize === 'string' && (currentTemplateSize as string).startsWith('custom:') && templates[currentTemplateSize as string];
      const dims = getTemplate(currentTemplateSize);
      
      if (isEditingExisting) {
        // Update existing template
        const t = templates[currentTemplateSize as string];
        templates[currentTemplateSize as string] = {
          ...t,
          cols: dims.cols,
          rows: dims.rows,
          updatedAt: Date.now(),
          layout: currentLayout,
        };
        saveCustomTemplates(templates);
        setCustomTemplates(templates);
        window.alert(`Template "${t.name}" saved.`);
        onClose();
        return;
      } else {
        // Create new template (Save As)
        const templateName = window.prompt('Enter template name:');
        if (!templateName || !templateName.trim()) {
          return;
        }
        const newTemplate = createCustomTemplate(
          templateName.trim(),
          dims.cols,
          dims.rows,
          `Custom template with ${stats.totalPorts} ports`
        );
        newTemplate.layout = currentLayout;
        templates[newTemplate.id] = newTemplate;
        saveCustomTemplates(templates);
        setCustomTemplates(templates);
        window.alert(`Template "${templateName.trim()}" created successfully!`);
        onClose();
        return;
      }
    }
    
    // Show warning if layout is overwritten (skip when only electrical fields changed)
    if (layoutHasChanges && initialLayout && (initialLayout.simple || initialLayout.multiFace)) {
      setPendingSaveTarget(saveTarget);
      setShowSaveConfirmation(true);
      return;
    }
    
    // Proceed with save
    performSave(saveTarget);
  };

  const attachElectrical = (layout: any, equipmentData?: any) => {
    if (!showElectrical) return equipmentData;
    const liveDraft = blueprintGraphCommitRef.current?.() || deviceModelDraft;
    if (!liveDraft) return equipmentData;
    const finalized = finalizeDeviceModel({
      ...liveDraft,
      equipmentId: liveDraft.equipmentId || equipmentId || 'custom',
      layout: {
        ...liveDraft.layout,
        ...(layout.simple ? { simple: layout.simple } : {}),
        ...(layout.multiFace ? { multiFace: layout.multiFace } : {}),
        numberingSchemeId: layout.numberingSchemeId,
      },
      electricalGraph: liveDraft.electricalGraph,
    });
    return { ...(equipmentData || {}), deviceModel: finalized };
  };

  // Perform the actual save operation
  const performSave = (saveTarget?: 'instance' | 'library') => {
    const target = saveTarget ?? pendingSaveTarget;
    if (isMultiFaceMode) {
      // Save multi-face layout
      const faces: any = {};
      
      // Convert each face's grid to layout using the face's specific template size
      Object.entries(faceGrids).forEach(([face, gridCells]) => {
        const hasAnyPorts = gridCells.some(cell => cell.type !== 'empty');
        if (hasAnyPorts) {
          const faceKey = face as EquipmentFace;
          const faceTemplateSize = faceTemplateSizes[faceKey];
          faces[face] = gridToPortLayout(gridCells, faceTemplateSize);
        }
      });
      
      const layout = {
        numberingSchemeId,
        multiFace: {
          faces,
          defaultFace: activeFace,
        },
        // Keep a simple mirror of the default/front face for catalog-style loads.
        ...(faces[activeFace] || faces.front
          ? { simple: faces[activeFace] || faces.front }
          : {}),
      };
      
      if (isCreatingNew) {
        const equipmentData = attachElectrical(layout, {
          name: newEquipmentName.trim(),
          manufacturer: selectedManufacturer,
          type: selectedCategory as any,
          price: equipmentPrice ? parseFloat(equipmentPrice) : undefined,
          icon: selectedIcon,
          specifications: {
            ports: stats.totalPorts,
          },
        });
        onSave(layout, equipmentData, target);
      } else {
        onSave(layout, attachElectrical(layout), target);
      }
    } else {
      const layout = {
        numberingSchemeId,
        simple: gridToPortLayout(grid, templateSize),
      };
      
      if (isCreatingNew) {
        const equipmentData = attachElectrical(layout, {
          name: newEquipmentName.trim(),
          manufacturer: selectedManufacturer,
          type: selectedCategory as any,
          price: equipmentPrice ? parseFloat(equipmentPrice) : undefined,
          icon: selectedIcon,
          specifications: {
            ports: stats.totalPorts,
          },
        });
        onSave(layout, equipmentData, target);
      } else {
        onSave(layout, attachElectrical(layout), target);
      }
    }
    setShowSaveConfirmation(false);
    setPendingSaveTarget(undefined);
  };

  // Helper function to get port type info (including custom types)
  const getPortTypeInfo = (type: PortKind | 'empty' | 'blank') => {
    if (type === 'empty') {
      return PORT_TYPES.empty;
    }
    if (type === 'blank') {
      return PORT_TYPES.blank;
    }
    
    // Check if it's a custom port type
    if (typeof type === 'string' && type.startsWith('custom:')) {
      const customId = type.replace('custom:', '');
      const customType = customPortTypes[customId];
      if (customType) {
        const iconComponent = AVAILABLE_ICONS.find(icon => icon.name === customType.icon)?.component || Box;
        return {
          label: customType.name,
          color: customType.color,
          icon: iconComponent,
          description: `Custom port type: ${customType.name}`,
        };
      }
    }
    
    // Return standard port type
    return PORT_TYPES[type as keyof typeof PORT_TYPES] || PORT_TYPES.custom;
  };

  // Get cell style based on port type
  const getCellStyle = (type: PortKind) => {
    const portType = getPortTypeInfo(type);
    
    if (type === 'blank') {
      return 'bg-transparent border border-dashed border-slate-600/50 hover:border-slate-400';
    }
    if (type === 'empty') {
      return 'bg-gray-200 border-gray-300 hover:bg-gray-300';
    }
    
    // Create gradient based on port color
    return `border-2 hover:scale-105 transition-all`;
  };
  
  // Get cell background color
  const getCellColor = (type: PortKind) => {
    if (isVacantPortType(type)) return undefined;
    return getPortTypeInfo(type).color;
  };

  const stampFacedPortsOntoGrids = (nextPorts: FacedPort[], asBaseline = false) => {
    const keyOf = (face: string, number: number, kind: string) => `${face}:${number}:${kind}`;
    const byKey = new Map<string, FacedPort>();
    nextPorts.forEach((p) => byKey.set(keyOf(p.face, p.number, String(p.kind)), p));
    const stamp = (cells: GridCell[], face: EquipmentFace): GridCell[] =>
      cells.map((cell) => {
        if (isVacantPortType(cell.type) || typeof cell.number !== 'number') {
          return {
            ...cell,
            poeIn: undefined,
            poeOut: undefined,
            poeInRoleId: undefined,
            poeOutRoleId: undefined,
            passthrough: undefined,
          };
        }
        const match = byKey.get(keyOf(face, cell.number, String(cell.type)));
        if (!match) {
          return {
            ...cell,
            poeIn: undefined,
            poeOut: undefined,
            poeInRoleId: undefined,
            poeOutRoleId: undefined,
            passthrough: undefined,
          };
        }
        return {
          ...cell,
          poeIn: match.poeIn,
          poeOut: match.poeOut,
          poeInRoleId: match.poeInRoleId,
          poeOutRoleId: match.poeOutRoleId,
          passthrough: match.passthrough,
          speedsMbps: match.speedsMbps ?? cell.speedsMbps,
        };
      });
    if (isMultiFaceMode) {
      setFaceGrids((prev) => {
        const next = { ...prev };
        (Object.keys(next) as EquipmentFace[]).forEach((face) => {
          next[face] = stamp(next[face], face);
        });
        if (asBaseline) {
          initialFaceGridsRef.current = JSON.parse(JSON.stringify(next));
        }
        return next;
      });
    } else {
      setGrid((prev) => {
        const stamped = stamp(prev, 'front');
        if (asBaseline) {
          initialGridRef.current = JSON.parse(JSON.stringify(stamped));
        }
        return stamped;
      });
    }
  };

  const overlayElectricalOnCells = (cells: GridCell[], face: EquipmentFace, prevPorts: FacedPort[]): GridCell[] =>
    cells.map((cell) => {
      if (isVacantPortType(cell.type) || typeof cell.number !== 'number') return cell;
      const match = prevPorts.find(
        (p) => (p.face || 'front') === face && p.number === cell.number && String(p.kind) === String(cell.type)
      );
      if (!match) return cell;
      return {
        ...cell,
        poeIn: match.poeIn,
        poeOut: match.poeOut,
        poeInRoleId: match.poeInRoleId,
        poeOutRoleId: match.poeOutRoleId,
        passthrough: match.passthrough,
      };
    });

  const liveLayoutForElectrical = useMemo(() => {
    if (isMultiFaceMode) {
      const faces: any = {};
      Object.entries(faceGrids).forEach(([face, gridCells]) => {
        faces[face] = gridToPortLayout(gridCells, faceTemplateSizes[face as EquipmentFace]);
      });
      return { numberingSchemeId, multiFace: { faces, defaultFace: activeFace } };
    }
    return { numberingSchemeId, simple: gridToPortLayout(grid, templateSize) };
  }, [isMultiFaceMode, faceGrids, faceTemplateSizes, grid, templateSize, numberingSchemeId, activeFace]);

  const liveElectricalPorts = useMemo(
    () => collectLayoutPorts(liveLayoutForElectrical),
    [liveLayoutForElectrical]
  );
  const liveElectricalPortsRef = useRef(liveElectricalPorts);
  liveElectricalPortsRef.current = liveElectricalPorts;

  const electricalValidation = useMemo(
    () => (deviceModelDraft ? validateDeviceModel(deviceModelDraft) : { ok: true, errors: [] as string[], warnings: [] as string[] }),
    [deviceModelDraft]
  );

  const deviceModelDraftRef = useRef(deviceModelDraft);
  deviceModelDraftRef.current = deviceModelDraft;

  useEffect(() => {
    if (!showElectrical) return;
    if (deviceModelDraftRef.current) return;
    const type = equipmentType || selectedCategory;
    if (!type) return;
    const inferred = inferModelFromLayout(equipmentId || 'custom', type, initialLayout);
    const seeded = initElectricalDraft(inferred);
    setDeviceModelDraft(seeded);
    if (!initialDeviceModelJsonRef.current) initialDeviceModelJsonRef.current = JSON.stringify(seeded);
  }, [showElectrical, equipmentType, selectedCategory, equipmentId, initialLayout]);

  useEffect(() => {
    if (!showElectrical || !deviceModelDraftRef.current || !hasInitializedRef.current) return;
    const merged = mergePortsIntoDraft(deviceModelDraftRef.current, liveElectricalPorts);
    const nextJson = JSON.stringify(merged.draft);
    if (nextJson !== JSON.stringify(deviceModelDraftRef.current)) {
      setDeviceModelDraft(merged.draft);
    }
    if (!electricalBaselineReadyRef.current) {
      electricalBaselineReadyRef.current = true;
      initialDeviceModelJsonRef.current = nextJson;
    }
  }, [showElectrical, liveElectricalPorts]);

  useEffect(() => {
    if (!isPortPlaceOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (portPlaceBtnRef.current?.contains(t) || portPlaceMenuRef.current?.contains(t)) return;
      setIsPortPlaceOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isPortPlaceOpen]);

  const hoverElectricalPort = (layoutKey: string | null, face?: EquipmentFace) => {
    setHoverPortId(layoutKey);
    if (layoutKey && face && face !== activeFace) setActiveFace(face);
  };

  const catalogStampRef = useRef(false);
  useEffect(() => {
    if (!showElectrical || catalogStampRef.current) return;
    const t = window.setTimeout(() => {
      const draft = deviceModelDraftRef.current;
      if (catalogStampRef.current) return;
      catalogStampRef.current = true;
      if (!draft) return;
      const fromModel = collectLayoutPorts(draft.layout);
      if (!fromModel.some((p) => p.poeIn || p.poeOut)) {
        if (!electricalBaselineReadyRef.current) {
          electricalBaselineReadyRef.current = true;
          initialDeviceModelJsonRef.current = JSON.stringify(draft);
        }
        return;
      }
      const live = liveElectricalPortsRef.current;
      const overlaid = live.map((p) => {
        if (p.poeIn || p.poeOut || p.passthrough) return p;
        const match =
          fromModel.find((m) => m.number === p.number && String(m.kind) === String(p.kind) && m.face === p.face) ||
          fromModel.find((m) => m.number === p.number && String(m.kind) === String(p.kind));
        if (!match || (!match.poeIn && !match.poeOut && !match.passthrough)) return p;
        return {
          ...p,
          poeIn: match.poeIn,
          poeOut: match.poeOut,
          poeInRoleId: match.poeInRoleId,
          poeOutRoleId: match.poeOutRoleId,
          passthrough: match.passthrough,
        };
      });
      stampFacedPortsOntoGrids(overlaid, true);
      const merged = mergePortsIntoDraft(draft, overlaid);
      setDeviceModelDraft(merged.draft);
      if (!electricalBaselineReadyRef.current) {
        electricalBaselineReadyRef.current = true;
        initialDeviceModelJsonRef.current = JSON.stringify(merged.draft);
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [showElectrical]);

  const portCountLines = useMemo(() => {
    const counts = new Map<string, { count: number; label: string; color: string }>();
    currentGrid.forEach((cell) => {
      if (isVacantPortType(cell.type)) return;
      const info = getPortTypeInfo(cell.type);
      const key = String(cell.type);
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { count: 1, label: info.label, color: info.color });
    });
    return Array.from(counts.values());
  }, [currentGrid, customPortTypes]);

  // Create custom port type
  const handleCreateCustomPort = () => {
    if (!newCustomPortName.trim()) {
      window.alert('Please enter a port type name');
      return;
    }
    
    const newPortType: CustomPortType = {
      id: `custom-port-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: newCustomPortName.trim(),
      icon: newCustomPortIcon,
      color: newCustomPortColor,
      createdAt: Date.now(),
    };
    
    const updated = { ...customPortTypes, [newPortType.id]: newPortType };
    setCustomPortTypes(updated);
    saveCustomPortTypes(updated);
    
    // Select the newly created port type
    setSelectedPortType(`custom:${newPortType.id}` as PortKind);
    
    // Close the menu and reset form
    setIsCreatingCustomPort(false);
    setIsCustomPortDropdownOpen(false);
    setNewCustomPortName('');
    setNewCustomPortIcon('Box');
    setNewCustomPortColor('#64748b');
  };

  // Delete custom port type
  const handleDeleteCustomPort = (portTypeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPortTypeToDelete(portTypeId);
    setShowDeleteCustomPortConfirm(true);
  };

  const confirmDeleteCustomPort = () => {
    if (!portTypeToDelete) return;
    
    const updated = { ...customPortTypes };
    delete updated[portTypeToDelete];
    setCustomPortTypes(updated);
    saveCustomPortTypes(updated);
    
    // If the deleted port type was selected, reset to default
    if (selectedPortType === `custom:${portTypeToDelete}`) {
      setSelectedPortType('rj45');
    }
    
    setShowDeleteCustomPortConfirm(false);
    setPortTypeToDelete(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Close custom port dropdown list
      if (isCustomPortDropdownOpen && customPortDropdownRef.current && !customPortDropdownRef.current.contains(target)) {
        setIsCustomPortDropdownOpen(false);
      }
      
      // Close create custom port menu
      if (isCreatingCustomPort) {
        // Check if click is outside the menu and not on the button
        const isClickOnMenu = customPortMenuRef.current && customPortMenuRef.current.contains(target);
        const isClickOnButton = customPortButtonRef.current && customPortButtonRef.current.contains(target);
        
        // Also check if clicking on modal content (but not the menu itself)
        const isClickOnModalContent = target.closest('.bg-slate-800\\/90.backdrop-blur-sm.rounded-lg.shadow-xl') && !isClickOnMenu;
        
        // Close if click is outside both the menu and the button, or if clicking on modal content
        if ((!isClickOnMenu && !isClickOnButton) || isClickOnModalContent) {
          setIsCreatingCustomPort(false);
          setCustomPortMenuPosition(null);
        }
      }
    };

    if (isCustomPortDropdownOpen || isCreatingCustomPort) {
      // Use a small delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true); // Use capture phase
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isCustomPortDropdownOpen, isCreatingCustomPort]);

  // Click-outside handler for Add New Template menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      if (isAddNewTemplateMenuOpen) {
        const isClickOnMenu = addNewTemplateMenuRef.current && addNewTemplateMenuRef.current.contains(target);
        const isClickOnSelect = templateSelectRef.current && templateSelectRef.current.contains(target);
        
        if (!isClickOnMenu && !isClickOnSelect) {
          setIsAddNewTemplateMenuOpen(false);
          setAddNewTemplateMenuPosition(null);
        }
      }
    };

    if (isAddNewTemplateMenuOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isAddNewTemplateMenuOpen]);

  // Click-outside handler for Manufacturer dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      if (isManufacturerDropdownOpen && manufacturerDropdownRef.current && !manufacturerDropdownRef.current.contains(target)) {
        setIsManufacturerDropdownOpen(false);
        setIsCreatingManufacturer(false);
        setNewManufacturerName('');
      }
      
      if (isCategoryDropdownOpen && categoryDropdownRef.current && !categoryDropdownRef.current.contains(target)) {
        setIsCategoryDropdownOpen(false);
        setIsCreatingCategory(false);
        setNewCategoryName('');
      }
      
      if (isIconDropdownOpen && iconDropdownRef.current && !iconDropdownRef.current.contains(target)) {
        setIsIconDropdownOpen(false);
      }
    };

    if (isManufacturerDropdownOpen || isCategoryDropdownOpen || isIconDropdownOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isManufacturerDropdownOpen, isCategoryDropdownOpen, isIconDropdownOpen]);

  // Template management functions
  const handleCreateTemplate = () => {
    if (!newTemplateName.trim()) {
      window.alert('Please enter a template name');
      return;
    }
    
    if (newTemplateCols < 1 || newTemplateRows < 1) {
      window.alert('Rows and columns must be at least 1');
      return;
    }
    
    if (newTemplateCols > 64 || newTemplateRows > 64) {
      window.alert('Maximum size is 64 columns × 64 rows');
      return;
    }
    
    const newTemplate = createCustomTemplate(
      newTemplateName.trim(),
      newTemplateCols,
      newTemplateRows,
      newTemplateDescription.trim() || undefined
    );
    
    // If we're saving current as a template, attach current layout
    if (isSavingCurrentAsTemplate) {
      const currentLayout = withNumberingScheme(isMultiFaceMode ? {
        multiFace: {
          faces: Object.fromEntries(
            Object.entries(faceGrids).map(([face, gridCells]) => {
              const faceKey = face as EquipmentFace;
              const faceTemplateSize = faceTemplateSizes[faceKey];
              return [face, gridToPortLayout(gridCells, faceTemplateSize)];
            })
          ),
          defaultFace: activeFace,
        },
      } : {
        simple: gridToPortLayout(grid, templateSize),
      });
      newTemplate.layout = currentLayout;
    }
    
    const templates = loadCustomTemplates();
    templates[newTemplate.id] = newTemplate;
    saveCustomTemplates(templates);
    setCustomTemplates(templates);
    
    // Close the menu and reset form (DO NOT auto-select the new template)
    setIsAddNewTemplateMenuOpen(false);
    setAddNewTemplateMenuPosition(null);
    setIsCreatingTemplate(false);
    setIsSavingCurrentAsTemplate(false);
    setNewTemplateName('');
    setNewTemplateCols(12);
    setNewTemplateRows(2);
    setNewTemplateDescription('');
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    
    deleteCustomTemplate(templateId);
    setCustomTemplates(loadCustomTemplates());
    
    // If the deleted template was in use, switch to desktop
    const defaultTemplate: TemplateSize = 'desktop';
    if (isMultiFaceMode) {
      if (faceTemplateSizes[activeFace] === templateId) {
        setFaceTemplateSizes(prev => ({
          ...prev,
          [activeFace]: defaultTemplate,
        }));
      }
    } else {
      if (templateSize === templateId) {
        setTemplateSize(defaultTemplate);
      }
    }
  };

  const describeSchemePrefixes = (scheme: PortNamingScheme) => {
    return NAMING_SCHEME_PREFIX_KINDS.map(({ key, label }) => {
      const prefix = (scheme.prefixes[key] || '').trim();
      return prefix ? `${label} → ${prefix}` : `${label} → number`;
    }).join(', ');
  };

  const resetSchemeForm = () => {
    setEditingSchemeId(null);
    setNewSchemeName('');
    setNewSchemePrefixes(emptySchemePrefixes());
  };

  const startEditScheme = (scheme: PortNamingScheme) => {
    setEditingSchemeId(scheme.id);
    setNewSchemeName(scheme.name);
    setNewSchemePrefixes({
      rj45: scheme.prefixes.rj45 || '',
      sfp: scheme.prefixes.sfp || '',
      sfpPlus: scheme.prefixes.sfpPlus || '',
    });
  };

  const handleSaveNamingScheme = () => {
    const name = newSchemeName.trim();
    if (!name) {
      window.alert('Please enter a scheme name');
      return;
    }
    const prefixes: Record<string, string> = {};
    Object.entries(newSchemePrefixes).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (trimmed) prefixes[key] = trimmed;
    });
    const id = editingSchemeId || `custom:${Date.now().toString(36)}`;
    const existing = editingSchemeId ? customNamingSchemes[editingSchemeId] : undefined;
    const next = {
      ...customNamingSchemes,
      [id]: {
        id,
        name,
        prefixes,
        createdAt: existing?.createdAt || Date.now(),
      },
    };
    setCustomNamingSchemes(next);
    saveCustomPortNamingSchemes(next);
    setNumberingSchemeId(id);
    resetSchemeForm();
  };

  const handleDeleteNamingScheme = (schemeId: string) => {
    if (!window.confirm('Delete this naming scheme?')) return;
    const next = { ...customNamingSchemes };
    delete next[schemeId];
    setCustomNamingSchemes(next);
    saveCustomPortNamingSchemes(next);
    if (numberingSchemeId === schemeId) {
      setNumberingSchemeId(NUMBERS_ONLY_SCHEME_ID);
    }
    if (editingSchemeId === schemeId) {
      resetSchemeForm();
    }
  };

  const handleSaveCurrentAsTemplate = () => {
    const template = getTemplate(currentTemplateSize);
    setNewTemplateName(`${template.name} Copy`);
    setNewTemplateCols(template.cols);
    setNewTemplateRows(template.rows);
    setNewTemplateDescription(template.description);
    setIsSavingCurrentAsTemplate(true);
    setIsCreatingTemplate(true);
  };

  const handleCreateNewTemplate = () => {
    setNewTemplateName('');
    setNewTemplateCols(12);
    setNewTemplateRows(2);
    setNewTemplateDescription('');
    setIsSavingCurrentAsTemplate(false);
    setIsCreatingTemplate(true);
  };

  // Export/Import handlers
  const handleExportClick = () => {
    // Prepare current layout
    const currentLayout = withNumberingScheme(isMultiFaceMode ? {
      multiFace: {
        faces: Object.fromEntries(
          Object.entries(faceGrids).map(([face, gridCells]) => {
            const hasAnyPorts = gridCells.some(cell => cell.type !== 'empty');
            if (hasAnyPorts) {
              const faceKey = face as EquipmentFace;
              const faceTemplateSize = faceTemplateSizes[faceKey];
              return [face, gridToPortLayout(gridCells, faceTemplateSize)];
            }
            return null;
          }).filter(Boolean) as [string, any][]
        ),
        defaultFace: activeFace,
      },
    } : {
      simple: gridToPortLayout(grid, templateSize),
    });

    // Check if there's existing layout to prefill metadata
    if (initialLayout?.simple || initialLayout?.multiFace) {
      // Keep existing metadata
    }

    setIsExportModalOpen(true);
  };

  const handleExport = () => {
    try {
      // Get current layout
      const currentLayout = withNumberingScheme(isMultiFaceMode ? {
        multiFace: {
          faces: Object.fromEntries(
            Object.entries(faceGrids).map(([face, gridCells]) => {
              const hasAnyPorts = gridCells.some(cell => cell.type !== 'empty');
              if (hasAnyPorts) {
                const faceKey = face as EquipmentFace;
                const faceTemplateSize = faceTemplateSizes[faceKey];
                return [face, gridToPortLayout(gridCells, faceTemplateSize)];
              }
              return null;
            }).filter(Boolean) as [string, any][]
          ),
          defaultFace: activeFace,
        },
      } : {
        simple: gridToPortLayout(grid, templateSize),
      });

      // Export equipment file
      const equipmentFile = exportEquipment(currentLayout, exportMetadata);
      
      // Download file
      const filename = exportMetadata.name.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'equipment_layout';
      downloadEquipmentFile(equipmentFile, filename);
      
      // Close modal
      setIsExportModalOpen(false);
      
      // Show success message briefly
      setImportSuccess(true);
      setTimeout(() => setImportSuccess(false), 2000);
    } catch (error) {
      window.alert(`Failed to export equipment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file extension
    if (!file.name.toLowerCase().endsWith('.npb')) {
      setImportError('Invalid file type. Please select a .npb file.');
      setIsImporting(false);
      return;
    }

    setIsImporting(true);
    setImportError(null);
    setImportSuccess(false);

    try {
      const { layout, templates, metadata } = await importEquipment(file);

      // Import custom templates if any
      if (templates && Object.keys(templates).length > 0) {
        const existingTemplates = loadCustomTemplates();
        Object.entries(templates).forEach(([id, template]) => {
          // Only import if it doesn't exist or is newer
          if (!existingTemplates[id] || (existingTemplates[id].updatedAt < template.updatedAt)) {
            existingTemplates[id] = template;
          }
        });
        saveCustomTemplates(existingTemplates);
        setCustomTemplates(existingTemplates);
      }

      // Update metadata if provided
      if (metadata?.name) {
        setExportMetadata(prev => ({
          ...prev,
          name: metadata.name || prev.name,
          description: metadata.description || prev.description,
          manufacturer: metadata.manufacturer || prev.manufacturer,
          model: metadata.model || prev.model,
        }));
      }

      // Load the imported layout without wiping electrical roles / non-PoE power inputs
      const prevElectrical = liveElectricalPortsRef.current;
      if (typeof layout.numberingSchemeId === 'string' && layout.numberingSchemeId) {
        setNumberingSchemeId(layout.numberingSchemeId);
      }
      if (layout.multiFace) {
        // Multi-face layout
        setIsMultiFaceMode(true);
        
        const newFaceGrids: Record<EquipmentFace, GridCell[]> = {
          front: [],
          back: [],
          left: [],
          right: [],
          top: [],
          bottom: [],
        };
        const newFaceTemplateSizes: Record<EquipmentFace, TemplateSize> = {
          front: defaultTemplate,
          back: defaultTemplate,
          left: defaultTemplate,
          right: defaultTemplate,
          top: defaultTemplate,
          bottom: defaultTemplate,
        };

        Object.entries(layout.multiFace.faces).forEach(([face, faceLayout]: [string, any]) => {
          const faceKey = face as EquipmentFace;
          let faceTemplateSize = templateIdFromSimpleLayout(faceLayout, portCount) as TemplateSize;
          // Migrate old template IDs
          if (typeof faceTemplateSize === 'string' && needsTemplateMigration(faceTemplateSize)) {
            faceTemplateSize = migrateTemplateId(faceTemplateSize);
          }
          newFaceTemplateSizes[faceKey] = faceTemplateSize as TemplateSize;

          if (faceLayout?.ports && Array.isArray(faceLayout.ports)) {
            newFaceGrids[faceKey] = cellsFromSavedLayout(faceLayout);
          } else {
            const faceTemplate = getTemplate(faceTemplateSize as TemplateSize);
            newFaceGrids[faceKey] = createEmptyGrid(faceTemplate.rows, faceTemplate.cols);
          }
        });

        setFaceGrids(
          Object.fromEntries(
            (Object.keys(newFaceGrids) as EquipmentFace[]).map((face) => [
              face,
              overlayElectricalOnCells(newFaceGrids[face], face, prevElectrical),
            ])
          ) as Record<EquipmentFace, GridCell[]>
        );
        setFaceTemplateSizes(newFaceTemplateSizes);
        
        if (layout.multiFace.defaultFace) {
          setActiveFace(layout.multiFace.defaultFace);
        }
      } else if (layout.simple) {
        // Single-face layout
        setIsMultiFaceMode(false);
        
        let nextTemplateSize = templateIdFromSimpleLayout(layout.simple, portCount) as TemplateSize;
        // Migrate old template IDs
        if (typeof nextTemplateSize === 'string' && needsTemplateMigration(nextTemplateSize)) {
          nextTemplateSize = migrateTemplateId(nextTemplateSize);
        }
        setTemplateSize(nextTemplateSize as TemplateSize);
        
        if (layout.simple.ports && Array.isArray(layout.simple.ports)) {
          const loadedGrid = cellsFromSavedLayout(layout.simple);
          setGrid(overlayElectricalOnCells(loadedGrid, 'front', prevElectrical));
        } else {
          // No ports, create empty grid
          const template = getTemplate(nextTemplateSize as TemplateSize);
          setGrid(overlayElectricalOnCells(createEmptyGrid(template.rows, template.cols), 'front', prevElectrical));
        }
      }

      setImportSuccess(true);
      setTimeout(() => {
        setImportSuccess(false);
        setIsImporting(false);
      }, 2000);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Failed to import equipment file');
      setIsImporting(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Change template and preserve existing ports (or load saved layout for custom templates)
  const handleTemplateChange = (newTemplate: TemplateSize) => {
    if (isMultiFaceMode) {
      // Update only the active face's template size
      setFaceTemplateSizes(prev => ({
        ...prev,
        [activeFace]: newTemplate,
      }));
      
      // Handle 'none' case - clear the grid
      if (newTemplate === 'none') {
        setFaceGrids(prev => ({
          ...prev,
          [activeFace]: [],
        }));
        return;
      }

      // If custom template with saved layout, load it for the active face
      if (typeof newTemplate === 'string' && newTemplate.startsWith('custom:')) {
        const t = customTemplates[newTemplate];
        const faceLayout = t?.layout?.multiFace?.faces?.[activeFace] || undefined;
        if (faceLayout?.ports && Array.isArray(faceLayout.ports)) {
          const faceTemplate = getTemplate(newTemplate);
          const faceStageHeight = faceTemplate.rows * (PORT.height + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP + 44;
          const loaded = loadGridFromLayout(faceLayout.ports, faceTemplate.rows, faceTemplate.cols, faceStageHeight, faceLayout.blanks);
          setFaceGrids(prev => ({ ...prev, [activeFace]: loaded }));
          if (typeof t.layout?.numberingSchemeId === 'string') {
            setNumberingSchemeId(t.layout.numberingSchemeId);
          }
          return;
        }
      }
      
      // Resize the active face's grid
      const newTemplateData = getTemplate(newTemplate);
      const currentFaceGrid = faceGrids[activeFace];
      const newCells = createEmptyGrid(newTemplateData.rows, newTemplateData.cols);
      
      // Try to preserve existing ports by mapping them to new grid
      currentFaceGrid.forEach(oldCell => {
        if (oldCell.type !== 'empty' && 
            oldCell.row < newTemplateData.rows && 
            oldCell.col < newTemplateData.cols) {
          const newIndex = oldCell.row * newTemplateData.cols + oldCell.col;
          if (newIndex < newCells.length && newCells[newIndex]) {
            newCells[newIndex].type = oldCell.type;
            newCells[newIndex].number = oldCell.number;
          }
        }
      });
      
      setFaceGrids(prev => ({
        ...prev,
        [activeFace]: fillMissingPortNumbers(newCells),
      }));
    } else {
      // Single-face mode - existing behavior
      // If selecting a custom template with saved layout, load it
      if (typeof newTemplate === 'string' && newTemplate.startsWith('custom:')) {
        const t = customTemplates[newTemplate];
        if (t?.layout?.simple?.ports && Array.isArray(t.layout.simple.ports)) {
          const tmpl = getTemplate(newTemplate);
          const stageHeight = tmpl.rows * (PORT.height + ACTUAL_PORT_GAP) - ACTUAL_PORT_GAP + 44;
          const loaded = loadGridFromLayout(t.layout.simple.ports, tmpl.rows, tmpl.cols, stageHeight, t.layout.simple.blanks);
          setGrid(loaded);
          setTemplateSize(newTemplate);
          if (typeof t.layout?.numberingSchemeId === 'string') {
            setNumberingSchemeId(t.layout.numberingSchemeId);
          }
          return;
        }
      }
      // Otherwise, preserve where possible
      const newTemplateData = getTemplate(newTemplate);
      const newCells: GridCell[] = [];
      for (let row = 0; row < newTemplateData.rows; row++) {
        for (let col = 0; col < newTemplateData.cols; col++) {
          const existingCell = grid.find(c => c.row === row && c.col === col);
          newCells.push({
            row,
            col,
            type: existingCell?.type || 'empty',
            number: existingCell && !isVacantPortType(existingCell.type) ? existingCell.number : undefined,
          });
        }
      }
      setGrid(fillMissingPortNumbers(newCells));
      setTemplateSize(newTemplate);
    }
  };

  // Handle opening Add New Template menu from button
  const handleAddNewTemplateClick = () => {
    // Open the menu positioned relative to the button
    if (addNewTemplateButtonRef.current) {
      const rect = addNewTemplateButtonRef.current.getBoundingClientRect();
      const menuHeight = 500; // Approximate menu height
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Position menu below if there's enough space, otherwise above
      let topPosition: number;
      if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
        topPosition = rect.bottom + 4;
      } else {
        topPosition = rect.top - menuHeight - 4;
      }
      
      // Ensure menu stays within viewport
      topPosition = Math.max(10, Math.min(topPosition, viewportHeight - menuHeight - 10));
      
      setAddNewTemplateMenuPosition({
        top: topPosition,
        left: Math.max(10, Math.min(rect.left, window.innerWidth - 380))
      });
    }
    setIsAddNewTemplateMenuOpen(true);
  };

  // Handle template selection - immediate change
  const handleTemplateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTemplate = e.target.value as TemplateSize;
    
    // If same template, do nothing
    if (newTemplate === currentTemplateSize) {
      return;
    }
    
    // Immediately apply template change
    handleTemplateChange(newTemplate);
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        // Close custom port menu if clicking on backdrop
        if (isCreatingCustomPort) {
          setIsCreatingCustomPort(false);
          setCustomPortMenuPosition(null);
        }
        // Close Add New Template menu if clicking on backdrop
        if (isAddNewTemplateMenuOpen) {
          setIsAddNewTemplateMenuOpen(false);
          setAddNewTemplateMenuPosition(null);
        }
        // Stop all mouse events from reaching the canvas
        e.stopPropagation();
      }}
      onMouseMove={(e) => {
        e.stopPropagation();
      }}
      onMouseUp={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        // Close custom port menu if clicking on backdrop
        if (isCreatingCustomPort) {
          setIsCreatingCustomPort(false);
          setCustomPortMenuPosition(null);
        }
        // Close Add New Template menu if clicking on backdrop
        if (isAddNewTemplateMenuOpen) {
          setIsAddNewTemplateMenuOpen(false);
          setAddNewTemplateMenuPosition(null);
        }
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
        e.stopPropagation();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div 
        className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 flex flex-col overflow-hidden" 
        style={{ width: 'min(1440px, 92vw)', maxWidth: '92vw', height: '82vh', maxHeight: '82vh' }}
        onMouseDown={(e) => {
          // Close custom port menu if clicking on modal content
          if (isCreatingCustomPort && customPortMenuRef.current && !customPortMenuRef.current.contains(e.target as Node)) {
            setIsCreatingCustomPort(false);
            setCustomPortMenuPosition(null);
          }
          // Close Add New Template menu if clicking on modal content
          if (isAddNewTemplateMenuOpen && addNewTemplateMenuRef.current && !addNewTemplateMenuRef.current.contains(e.target as Node)) {
            setIsAddNewTemplateMenuOpen(false);
            setAddNewTemplateMenuPosition(null);
          }
          e.stopPropagation();
        }}
        onMouseMove={(e) => {
          e.stopPropagation();
        }}
        onMouseUp={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Close custom port menu if clicking on modal content
          if (isCreatingCustomPort && customPortMenuRef.current && !customPortMenuRef.current.contains(e.target as Node)) {
            setIsCreatingCustomPort(false);
            setCustomPortMenuPosition(null);
          }
          // Close Add New Template menu if clicking on modal content
          if (isAddNewTemplateMenuOpen && addNewTemplateMenuRef.current && !addNewTemplateMenuRef.current.contains(e.target as Node)) {
            setIsAddNewTemplateMenuOpen(false);
            setAddNewTemplateMenuPosition(null);
          }
          e.stopPropagation();
        }}
        onDragStart={(e) => {
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
      >
        {/* Header */}
        <div className="flex flex-col flex-shrink-0">
          {/* Top row with title and buttons */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700/50">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-lg font-semibold text-white">
                {standalone ? 'Template Editor' : 'Edit layout & electrical'}
              </h3>
              {!standalone && (
                isCreatingNew ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={newEquipmentName}
                      onChange={(e) => setNewEquipmentName(e.target.value)}
                      placeholder="Equipment name"
                      className="text-sm font-semibold text-gray-300 italic bg-transparent border-none outline-none focus:outline-none placeholder-gray-500"
                      style={{ minWidth: '200px' }}
                    />
                    {!standalone && (
                      <button
                        type="button"
                        onClick={() => setShowLayoutPanel((v) => !v)}
                        className={`p-1 rounded shrink-0 ${showLayoutPanel ? 'bg-white/15 text-gray-100' : 'text-gray-400 hover:text-gray-100 hover:bg-white/10'}`}
                        title={showLayoutPanel ? 'Hide layout panel' : 'Show layout panel'}
                      >
                        {showLayoutPanel ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  </div>
                ) : equipmentName ? (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm text-gray-300 truncate" title={equipmentName}>
                      {equipmentName}
                    </span>
                    {equipmentManufacturer && (
                      <>
                        <span className="text-gray-500">•</span>
                        <span className="text-xs text-gray-400 capitalize">
                          {equipmentManufacturer}
                        </span>
                      </>
                    )}
                    {!standalone && (
                      <button
                        type="button"
                        onClick={() => setShowLayoutPanel((v) => !v)}
                        className={`p-1 rounded shrink-0 ${showLayoutPanel ? 'bg-white/15 text-gray-100' : 'text-gray-400 hover:text-gray-100 hover:bg-white/10'}`}
                        title={showLayoutPanel ? 'Hide layout panel' : 'Show layout panel'}
                      >
                        {showLayoutPanel ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  </div>
                ) : null
            )}
          </div>
          <div className="flex items-center gap-2">
            {showElectrical && (
              <div className="flex rounded-md overflow-hidden border border-slate-600 text-sm">
                <button
                  type="button"
                  className={`px-2.5 py-1.5 ${editorView === 'classic' ? 'bg-blue-600 text-white' : 'bg-slate-700/50 text-gray-300'}`}
                  onClick={() => {
                    blueprintGraphCommitRef.current?.();
                    setEditorView('classic');
                    try { sessionStorage.setItem(EDITOR_VIEW_KEY, 'classic'); } catch { /* ignore */ }
                  }}
                >
                  Layout
                </button>
                <button
                  type="button"
                  className={`px-2.5 py-1.5 ${editorView === 'blueprint' ? 'bg-blue-600 text-white' : 'bg-slate-700/50 text-gray-300'}`}
                  onClick={() => {
                    setEditorView('blueprint');
                    try { sessionStorage.setItem(EDITOR_VIEW_KEY, 'blueprint'); } catch { /* ignore */ }
                  }}
                >
                  Blueprint
                </button>
              </div>
            )}
            {/* Import/Export Buttons */}
            <button
              onClick={handleImportClick}
                className="px-4 py-2 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors flex items-center gap-1.5"
              title="Import equipment layout from .npb file"
              disabled={isImporting}
            >
                <Upload size={14} />
              Import
            </button>
            <button
              onClick={handleExportClick}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1.5"
              title="Export equipment layout to .npb file"
            >
                <Download size={14} />
              Export
            </button>
            <button 
              onClick={onClose}
                className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-300 hover:text-white"
            >
              <X size={18} />
            </button>
            </div>
          </div>
        </div>

        {/* Equipment Creation Fields (only when creating new) */}
        {isCreatingNew && (
          <div className="p-3 border-b border-slate-700/50 bg-slate-800/30 space-y-3">
            {/* Manufacturer and Category - Side by Side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">Manufacturer</label>
                <div className="relative" ref={manufacturerDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsManufacturerDropdownOpen(!isManufacturerDropdownOpen)}
                    className="w-full px-3 py-2 text-sm bg-slate-700/50 border border-slate-600 rounded-md text-white hover:bg-slate-700 transition-colors flex items-center justify-between"
                  >
                    <span className={selectedManufacturer ? 'capitalize' : 'text-gray-400'}>
                      {selectedManufacturer || 'Select or create manufacturer...'}
                    </span>
                    <ChevronDown size={14} className={`text-gray-400 transition-transform ${isManufacturerDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isManufacturerDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {availableManufacturers.map((mfr) => (
                        <button
                          key={mfr}
                          type="button"
                          onClick={() => {
                            setSelectedManufacturer(mfr);
                            setIsManufacturerDropdownOpen(false);
                            setIsCreatingManufacturer(false);
                          }}
                          className="w-full px-2.5 py-1 text-xs text-left text-white hover:bg-slate-700 capitalize"
                        >
                          {mfr}
                        </button>
                      ))}
                      <div className="border-t border-slate-600">
                        {isCreatingManufacturer ? (
                          <div className="p-2 space-y-2">
                            <input
                              type="text"
                              value={newManufacturerName}
                              onChange={(e) => setNewManufacturerName(e.target.value)}
                              placeholder="Enter manufacturer name"
                              className="w-full px-2 py-1.5 text-xs bg-slate-700 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newManufacturerName.trim()) {
                                  const newMfr = newManufacturerName.trim().toLowerCase();
                                  setSelectedManufacturer(newMfr);
                                  setIsManufacturerDropdownOpen(false);
                                  setIsCreatingManufacturer(false);
                                  setNewManufacturerName('');
                                } else if (e.key === 'Escape') {
                                  setIsCreatingManufacturer(false);
                                  setNewManufacturerName('');
                                }
                              }}
                            />
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  if (newManufacturerName.trim()) {
                                    const newMfr = newManufacturerName.trim().toLowerCase();
                                    setSelectedManufacturer(newMfr);
                                    setIsManufacturerDropdownOpen(false);
                                    setIsCreatingManufacturer(false);
                                    setNewManufacturerName('');
                                  }
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Create
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsCreatingManufacturer(false);
                                  setNewManufacturerName('');
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-slate-700 text-gray-300 rounded hover:bg-slate-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsCreatingManufacturer(true)}
                            className="w-full px-2.5 py-1 text-xs text-left text-blue-400 hover:bg-slate-700 flex items-center gap-1.5"
                          >
                            <Plus size={14} />
                            Create New Manufacturer
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Category Selection */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">Category</label>
                <div className="relative" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    className="w-full px-3 py-2 text-sm bg-slate-700/50 border border-slate-600 rounded-md text-white hover:bg-slate-700 transition-colors flex items-center justify-between"
                  >
                    <span className={selectedCategory ? 'capitalize' : 'text-gray-400'}>
                      {selectedCategory || 'Select or create category...'}
                    </span>
                    <ChevronDown size={14} className={`text-gray-400 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isCategoryDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {availableCategories.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(cat);
                            setIsCategoryDropdownOpen(false);
                            setIsCreatingCategory(false);
                          }}
                          className="w-full px-2.5 py-1 text-xs text-left text-white hover:bg-slate-700 capitalize"
                        >
                          {cat}
                        </button>
                      ))}
                      <div className="border-t border-slate-600">
                        {isCreatingCategory ? (
                          <div className="p-2 space-y-2">
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder="Enter category name"
                              className="w-full px-2 py-1.5 text-xs bg-slate-700 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newCategoryName.trim()) {
                                  const newCat = newCategoryName.trim().toLowerCase();
                                  setSelectedCategory(newCat);
                                  setIsCategoryDropdownOpen(false);
                                  setIsCreatingCategory(false);
                                  setNewCategoryName('');
                                } else if (e.key === 'Escape') {
                                  setIsCreatingCategory(false);
                                  setNewCategoryName('');
                                }
                              }}
                            />
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  if (newCategoryName.trim()) {
                                    const newCat = newCategoryName.trim().toLowerCase();
                                    setSelectedCategory(newCat);
                                    setIsCategoryDropdownOpen(false);
                                    setIsCreatingCategory(false);
                                    setNewCategoryName('');
                                  }
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Create
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsCreatingCategory(false);
                                  setNewCategoryName('');
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-slate-700 text-gray-300 rounded hover:bg-slate-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsCreatingCategory(true)}
                            className="w-full px-2.5 py-1 text-xs text-left text-blue-400 hover:bg-slate-700 flex items-center gap-1.5"
                          >
                            <Plus size={14} />
                            Create New Category
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Price and Icon - Side by Side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">Price</label>
                <input
                  type="number"
                  value={equipmentPrice}
                  onChange={(e) => setEquipmentPrice(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 text-sm bg-slate-700/50 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">Icon</label>
                <div className="relative" ref={iconDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsIconDropdownOpen(!isIconDropdownOpen)}
                    className="w-full px-3 py-2 text-sm bg-slate-700/50 border border-slate-600 rounded-md text-white hover:bg-slate-700 transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      {(() => {
                        const IconComponent = availableIcons.find(i => i.name === selectedIcon)?.component || Package;
                        return <IconComponent size={14} />;
                      })()}
                      <span>{selectedIcon}</span>
                    </div>
                    <ChevronDown size={14} className={`text-gray-400 transition-transform ${isIconDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isIconDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {availableIcons.map((icon) => {
                        const IconComponent = icon.component;
                        return (
                          <button
                            key={icon.name}
                            type="button"
                            onClick={() => {
                              setSelectedIcon(icon.name);
                              setIsIconDropdownOpen(false);
                            }}
                            className="w-full px-2.5 py-1 text-xs text-left text-white hover:bg-slate-700 flex items-center gap-2"
                          >
                            <IconComponent size={14} />
                            <span>{icon.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".npb"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* Import/Export Status Messages */}
        {importError && (
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-[120] bg-slate-800/95 backdrop-blur-sm border border-red-600/50 rounded-md p-2 shadow-lg flex items-center gap-1.5 max-w-md">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-medium text-red-300">Import Failed</div>
              <div className="text-[10px] text-red-400 mt-0.5">{importError}</div>
            </div>
            <button
              onClick={() => setImportError(null)}
              className="p-0.5 hover:bg-slate-700/50 rounded transition-colors text-gray-400 hover:text-white"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {importSuccess && (
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-[120] bg-slate-800/95 backdrop-blur-sm border border-blue-600/50 rounded-md p-2 shadow-lg flex items-center gap-1.5 max-w-md">
            <CheckCircle size={14} className="text-blue-400 flex-shrink-0" />
            <div className="text-xs font-medium text-blue-300">
              {isImporting ? 'Importing...' : 'Import successful!'}
            </div>
          </div>
        )}

        {isImporting && (
          <div className="absolute inset-0 z-[105] bg-black/40 flex items-center justify-center">
            <div className="bg-slate-800/95 backdrop-blur-sm rounded-md shadow-lg p-4 flex flex-col items-center gap-2 border border-slate-700/50">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <div className="text-xs font-medium text-gray-300">Importing...</div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden flex">
          <div className={`flex-1 min-w-0 min-h-0 flex flex-col ${showElectrical && editorView === 'blueprint' ? 'min-h-0 px-2 pt-1.5 pb-1.5' : 'overflow-hidden px-4 pt-3 pb-3'}`}>
          <div className={`flex-shrink-0 flex flex-wrap items-end gap-3 ${showElectrical && editorView === 'blueprint' ? 'mb-1.5' : 'mb-3'}`}>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-400">Face</span>
              <select
                value={activeFace}
                onChange={(e) => setActiveFace(e.target.value as EquipmentFace)}
                className="px-2.5 py-1.5 text-sm border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Select face to edit"
              >
                {(Object.keys(FACE_INFO) as EquipmentFace[]).map((face) => (
                  <option key={face} value={face}>{FACE_INFO[face].label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-xs text-gray-400">Template</span>
              <div className="flex items-center gap-1">
                <select
                  ref={templateSelectRef}
                  value={currentTemplateSize}
                  onChange={handleTemplateSelect}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="px-2.5 py-1.5 text-sm border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="none">No template selected</option>
                  {parseFittedGridId(String(currentTemplateSize)) ? (
                    <option value={currentTemplateSize}>
                      {getTemplate(currentTemplateSize).name}
                    </option>
                  ) : null}
                  <optgroup label="Built-in Templates">
                    {Object.entries(PRESET_TEMPLATES).map(([key, template]) => (
                      <option key={key} value={key}>{template.name}</option>
                    ))}
                  </optgroup>
                  {Object.keys(customTemplates).length > 0 && (
                    <optgroup label="Custom Templates">
                      {Object.values(customTemplates).map((template) => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <button
                  ref={addNewTemplateButtonRef}
                  onClick={handleAddNewTemplateClick}
                  className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
                  title="Add New Template"
                >
                  <PlusCircle size={16} className="text-blue-400" />
                </button>
                <button
                  onClick={() => setIsTemplateManagerOpen(true)}
                  className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
                  title="Manage Templates"
                >
                  <BookOpen size={16} />
                </button>
              </div>
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-400">Port to place</span>
              <button
                ref={portPlaceBtnRef}
                type="button"
                onClick={() => {
                  const r = portPlaceBtnRef.current?.getBoundingClientRect();
                  if (r) setPortPlaceMenuPos({ top: r.bottom + 4, left: r.left });
                  setIsPortPlaceOpen((v) => !v);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md border border-slate-600 bg-slate-700/50 text-white hover:bg-slate-700 min-w-[10rem]"
              >
                {(() => {
                  const info = getPortTypeInfo(selectedPortType);
                  const Icon = info.icon;
                  return (
                    <>
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: info.color === 'transparent' ? '#64748b' : info.color }} />
                      {Icon ? <Icon size={14} /> : null}
                      <span>{info.label}</span>
                      <ChevronDown size={14} className={`ml-auto transition-transform ${isPortPlaceOpen ? 'rotate-180' : ''}`} />
                    </>
                  );
                })()}
              </button>
              {isPortPlaceOpen && portPlaceMenuPos && createPortal(
                <div
                  ref={portPlaceMenuRef}
                  className="fixed z-[220] min-w-[16rem] max-h-[min(24rem,70vh)] overflow-y-auto rounded-md border border-slate-600 bg-slate-800 shadow-lg py-1"
                  style={{ top: portPlaceMenuPos.top, left: portPlaceMenuPos.left }}
                >
                  {Object.entries(PORT_TYPES).map(([key, portType]) => {
                    if (key === 'custom') return null;
                    const Icon = portType.icon;
                    const isSelected = selectedPortType === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setSelectedPortType(key as PortKind);
                          setIsPortPlaceOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${isSelected ? 'bg-blue-600 text-white' : 'text-gray-200 hover:bg-white/10'}`}
                      >
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: portType.color === 'transparent' ? '#64748b' : portType.color }} />
                        {Icon ? <Icon size={14} /> : <span className="w-3.5" />}
                        <span>{portType.label}</span>
                      </button>
                    );
                  })}
                  {Object.keys(customPortTypes).length > 0 && (
                    <div className="border-t border-slate-700 mt-1 pt-1">
                      <div className="px-3 py-1 text-xs text-gray-500">Custom</div>
                      {Object.values(customPortTypes).map((customType) => {
                        const key = `custom:${customType.id}`;
                        const Icon = AVAILABLE_ICONS.find((icon) => icon.name === customType.icon)?.component || Box;
                        const isSelected = selectedPortType === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              setSelectedPortType(key as PortKind);
                              setIsPortPlaceOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${isSelected ? 'bg-blue-600 text-white' : 'text-gray-200 hover:bg-white/10'}`}
                          >
                            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: customType.color }} />
                            <Icon size={14} />
                            <span>{customType.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="border-t border-slate-700 mt-1 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (portPlaceBtnRef.current) {
                          const rect = portPlaceBtnRef.current.getBoundingClientRect();
                          setCustomPortMenuPosition({ top: rect.bottom + 4, left: rect.left });
                        }
                        setIsCreatingCustomPort(true);
                        setIsPortPlaceOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-blue-300 hover:bg-white/10"
                    >
                      Create custom…
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
            <button
              type="button"
              onClick={fillAllRJ45}
              className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors whitespace-nowrap"
            >
              Fill All RJ45
            </button>
            <button
              type="button"
              onClick={() => {
                setIsDragMode((v) => {
                  const next = !v;
                  if (next) setIsNumberMode(false);
                  return next;
                });
              }}
              className="px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 border"
              style={isDragMode ? {
                backgroundColor: '#2563eb',
                color: 'white',
                borderColor: '#1e40af'
              } : {
                backgroundColor: '#475569',
                color: '#e2e8f0',
                borderColor: '#64748b'
              }}
              title="Enable drag mode to paint ports by dragging the mouse"
            >
              <Move size={14} />
              <span>Drag</span>
              {isDragMode && <CheckCircle size={14} />}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsNumberMode((v) => {
                  const next = !v;
                  if (next) setIsDragMode(false);
                  return next;
                });
              }}
              className="px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 border"
              style={isNumberMode ? {
                backgroundColor: '#2563eb',
                color: 'white',
                borderColor: '#1e40af'
              } : {
                backgroundColor: '#475569',
                color: '#e2e8f0',
                borderColor: '#64748b'
              }}
              title="Edit port numbers and naming scheme"
            >
              <span>Number</span>
              {isNumberMode && <CheckCircle size={14} />}
            </button>
            {isNumberMode && (
              <div className="flex items-center gap-1">
                <select
                  value={numberingSchemeId}
                  onChange={(e) => setNumberingSchemeId(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  title="Port naming scheme"
                >
                  {namingSchemes.map((scheme) => (
                    <option key={scheme.id} value={scheme.id}>
                      {scheme.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setEditingSchemeId(null);
                    setNewSchemeName('');
                    setNewSchemePrefixes(emptySchemePrefixes());
                    setIsSchemeManagerOpen(true);
                  }}
                  className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
                  title="Manage naming schemes"
                >
                  <BookOpen size={16} />
                </button>
              </div>
            )}
            {isMultiFaceMode && (
              <button
                onClick={clearFace}
                className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors whitespace-nowrap"
                title={`Clear ${FACE_INFO[activeFace].label} face`}
              >
                Clear Face
              </button>
            )}
            <div className="relative group">
              <button
                onClick={clearAll}
                className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors whitespace-nowrap"
              >
                Clear All
              </button>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                <div className="font-semibold mb-1">Warning</div>
                <div>This will clear all ports from {isMultiFaceMode ? 'all faces' : 'the layout'}. This action cannot be undone.</div>
              </div>
            </div>
            {showElectrical && editorView === 'blueprint' && deviceModelDraft && (
              <div className="flex items-center gap-2 flex-wrap ml-auto">
                {(['input', 'output', 'role'] as const).map((group) => (
                  <div key={group} className="relative">
                    <button
                      type="button"
                      className="px-2.5 py-1.5 text-sm bg-slate-700/50 text-gray-200 border border-slate-600 rounded-md whitespace-nowrap"
                      onClick={() => setPresetMenu((m) => (m === group ? null : group))}
                    >
                      {group === 'input' ? '+ Add Power Input' : group === 'output' ? '+ Add Power Output' : '+ Add PoE Rule'}
                    </button>
                    {presetMenu === group && (
                      <div className="absolute z-30 mt-1 right-0 w-56 rounded-md border border-slate-600 bg-slate-800 shadow-lg py-1">
                        {ELECTRICAL_PRESETS.filter((p) => p.group === group).map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-slate-700"
                            onClick={() => {
                              setDeviceModelDraft(applyElectricalPreset(deviceModelDraft, p.id));
                              setPresetMenu(null);
                            }}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isCreatingCustomPort && customPortMenuPosition && createPortal(
            <div
              ref={customPortMenuRef}
              className="fixed bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 z-[150] w-[320px] flex flex-col"
              style={{
                top: `${customPortMenuPosition.top}px`,
                left: `${customPortMenuPosition.left}px`,
                maxHeight: 'calc(100vh - 200px)'
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex-shrink-0 p-3 border-b border-slate-700/50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Create Custom Port Type</h3>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingCustomPort(false);
                    setNewCustomPortName('');
                    setNewCustomPortIcon('Box');
                    setNewCustomPortColor('#64748b');
                  }}
                  className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-300 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 min-h-0 p-3 space-y-3 overflow-y-auto">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Port Type Name *</label>
                  <input
                    type="text"
                    value={newCustomPortName}
                    onChange={(e) => setNewCustomPortName(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-slate-600 rounded-md bg-slate-700/50 text-white"
                    placeholder="e.g., Fiber Optic"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Icon</label>
                  <div className="grid grid-cols-6 gap-2 max-h-[200px] overflow-y-auto">
                    {AVAILABLE_ICONS.map((iconOption) => {
                      const IconComponent = iconOption.component;
                      const isSelected = newCustomPortIcon === iconOption.name;
                      return (
                        <button
                          key={iconOption.name}
                          type="button"
                          onClick={() => setNewCustomPortIcon(iconOption.name)}
                          className={`p-2 rounded-md border-2 transition-colors flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-900/30' : 'border-slate-600 bg-slate-700/50 hover:bg-slate-700'
                          }`}
                          title={iconOption.name}
                        >
                          <IconComponent size={16} className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={newCustomPortColor} onChange={(e) => setNewCustomPortColor(e.target.value)} className="w-12 h-8 rounded border border-slate-600 cursor-pointer" />
                    <input type="text" value={newCustomPortColor} onChange={(e) => setNewCustomPortColor(e.target.value)} className="flex-1 px-2.5 py-1.5 text-sm border border-slate-600 rounded-md bg-slate-700/50 text-white" />
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 p-3 border-t border-slate-700/50 flex justify-end gap-1.5">
                <button type="button" onClick={() => setIsCreatingCustomPort(false)} className="px-4 py-2 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md">Cancel</button>
                <button type="button" onClick={handleCreateCustomPort} disabled={!newCustomPortName.trim()} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md disabled:opacity-50">Create Port Type</button>
              </div>
            </div>,
            document.body
          )}

          {/* Scrollable Grid Area */}
          <div className="relative flex-1 min-h-0 flex flex-col">
          {(() => {
            const inner = (
              currentTemplateSize === 'none' ? (
                <div className="flex items-center justify-center border border-slate-600 rounded-md bg-slate-700/50" style={{ width: '400px', height: '200px' }}>
                  <p className="text-gray-400 text-center text-sm">
                    Please select a template or create one
                  </p>
                </div>
              ) : !showLayoutPanel ? (
                <div
                  className="flex items-center justify-center border border-slate-600 rounded-md bg-slate-800/40"
                  style={{ width: editorStageWidth, height: Math.min(editorStageHeight, 160) }}
                >
                  <p className="text-gray-400 text-sm">Layout panel hidden</p>
                </div>
              ) : (
                <div
                  className={`relative border border-slate-600 rounded-md select-none flex-shrink-0 ${showElectrical && editorView === 'blueprint' ? 'overflow-visible' : 'overflow-hidden'}`}
              style={{
                width: editorStageWidth,
                height: editorStageHeight,
                backgroundColor: '#1a1a1a',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onDragStart={(e) => e.preventDefault()}
              onMouseDown={(e) => {
                if (isDragMode) {
                  e.preventDefault(); // Prevent text selection
                }
              }}
            >
              <div 
                className="absolute grid select-none"
                style={{
                  gridTemplateColumns: `repeat(${template.cols}, ${EDITOR_CELL_SIZE}px)`,
                  gridTemplateRows: `repeat(${template.rows}, ${EDITOR_CELL_SIZE}px)`,
                  gap: `${EDITOR_CELL_GAP}px`,
                  padding: '10px',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                }}
                onDragStart={(e) => e.preventDefault()}
              >
                {currentGrid.map((cell, idx) => {
                      const portType = getPortTypeInfo(cell.type);
                  const Icon = portType.icon;
                  const poeLook = poeRj45Look({ kind: String(cell.type), poeIn: cell.poeIn, poeOut: cell.poeOut });
                  const cellPortId = typeof cell.number === 'number'
                    ? `p-${String(cell.type).replace(/[^a-zA-Z0-9_-]/g, '_')}-${cell.number}`
                    : '';
                  const isPoeHover = !!(hoverPortId && cellPortId && hoverPortId === cellPortId);
                  return (
                    <div
                      key={idx}
                      data-layout-cell="true"
                      {...(showElectrical && editorView === 'blueprint' && !isVacantPortType(cell.type)
                        ? {
                            'data-blueprint-port': 'true',
                            ...(typeof cell.number === 'number'
                              ? { 'data-graph-pin': portPinId(activeFace, cell.number, String(cell.type)) }
                              : {}),
                          }
                        : {})}
                      onClick={() => {
                        if (isNumberMode) {
                          if (!isVacantPortType(cell.type)) {
                            const isSame = activeNumberCell?.row === cell.row && activeNumberCell?.col === cell.col;
                            if (!isSame) {
                              const cellsForActivate = activeNumberCell ? commitNumberDraft(0) : currentGrid;
                              activateNumberCell(cell.row, cell.col, cellsForActivate);
                            }
                          }
                          return;
                        }
                        handleCellClick(cell.row, cell.col);
                      }}
                      onMouseDown={(e) => {
                        if (isDragMode) {
                          e.preventDefault(); // Prevent text selection
                        }
                        handleCellMouseDown(cell.row, cell.col);
                      }}
                      onMouseMove={() => handleCellMouseMove(cell.row, cell.col)}
                      onMouseUp={handleMouseUp}
                      draggable={false}
                      className={`
                        ${isDragMode || isNumberMode ? 'cursor-pointer' : 'cursor-pointer'} rounded transition-all relative
                        ${cell.type === 'blank' ? '' : 'hover:shadow-lg'} select-none
                        ${getCellStyle(cell.type)}
                        ${showElectrical && editorView === 'blueprint' && !isVacantPortType(cell.type) ? 'blueprint-port-cell' : ''}
                        ${isNumberMode && activeNumberCell?.row === cell.row && activeNumberCell?.col === cell.col ? 'ring-2 ring-blue-400' : ''}
                        ${isPoeHover ? 'ring-2 ring-yellow-400' : ''}
                      `}
                      style={{
                        width: EDITOR_CELL_SIZE,
                        height: EDITOR_CELL_SIZE,
                        backgroundColor: getCellColor(cell.type),
                        borderColor: isVacantPortType(cell.type)
                          ? undefined
                          : (String(cell.type) === 'rj45' ? poeRj45Border(poeLook) : portType.color),
                        borderWidth: isVacantPortType(cell.type) ? undefined : 2,
                        borderStyle: isVacantPortType(cell.type) ? undefined : 'solid',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (isVacantPortType(cell.type)) return;
                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        const fakePort = {
                          id: cellPortId || `cell-${idx}`,
                          number: typeof cell.number === 'number' ? cell.number : 0,
                          label: typeof cell.number === 'number'
                            ? formatPortLabel(numberingScheme, String(cell.type), cell.number)
                            : portType.label,
                          x: 0,
                          y: 0,
                          kind: cell.type as any,
                          poeIn: cell.poeIn,
                          poeOut: cell.poeOut,
                        };
                        const info = portHoverTooltip(fakePort, false);
                        setCellTooltip({ ...info, x: r.left + r.width / 2, y: r.top });
                      }}
                      onMouseLeave={() => setCellTooltip(null)}
                    >
                      {!isVacantPortType(cell.type) && (
                        isNumberMode && !peekPortTypes ? (
                          <div className="flex flex-col items-center justify-center h-full text-white px-0.5">
                            {(() => {
                              const prefix = prefixForPortKind(numberingScheme, String(cell.type));
                              const isActive = activeNumberCell?.row === cell.row && activeNumberCell?.col === cell.col;
                              const display = typeof cell.number === 'number'
                                ? formatPortLabel(numberingScheme, String(cell.type), cell.number)
                                : '';
                              if (isActive) {
                                return (
                                  <>
                                    {prefix ? (
                                      <div className="text-[8px] leading-none text-white/70 truncate max-w-full">{prefix}</div>
                                    ) : null}
                                    <input
                                      ref={numberInputRef}
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      value={numberDraft}
                                      onChange={(e) => setNumberDraft(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          commitNumberDraft(1);
                                        } else if (e.key === 'Tab') {
                                          e.preventDefault();
                                          commitNumberDraft(e.shiftKey ? -1 : 1);
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setNumberDraft(typeof cell.number === 'number' ? String(cell.number) : '');
                                        } else if (e.key === 'p' || e.key === 'P') {
                                          e.preventDefault();
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-full bg-transparent text-center text-[11px] font-semibold text-white outline-none p-0"
                                    />
                                  </>
                                );
                              }
                              return (
                                <div className={`font-semibold leading-tight text-center ${display.length > 5 ? 'text-[8px]' : 'text-[11px]'}`}>
                                  {display}
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                        <div className="flex flex-col items-center justify-center h-full text-white gap-0.5">
                          {poeLook !== 'data' ? <PoeRj45Marks look={poeLook} /> : null}
                          {Icon && <Icon size={14} className="opacity-90" />}
                          <div className="text-[9px] font-bold opacity-80">
                            {portType.label}
                          </div>
                        </div>
                        )
                      )}
                      {showElectrical && editorView === 'blueprint' && !isVacantPortType(cell.type) && typeof cell.number === 'number' ? (
                        <BlueprintPortPips pinId={portPinId(activeFace, cell.number, String(cell.type))} />
                      ) : null}
                    </div>
                  );
                })}
              </div>
                </div>
              )
            );
            if (showElectrical && editorView === 'blueprint' && deviceModelDraft) {
              return (
                <LayoutBlueprintCanvas
                  draft={deviceModelDraft}
                  onChange={setDeviceModelDraft}
                  ports={liveElectricalPorts}
                  onPortsChange={(next) => stampFacedPortsOntoGrids(next)}
                  activeFace={activeFace}
                  showBoard={showLayoutPanel && currentTemplateSize !== 'none'}
                  boardWidth={editorStageWidth}
                  boardHeight={editorStageHeight}
                  boardCols={template.cols}
                  cells={currentGrid.map((c) => ({ row: c.row, col: c.col, type: String(c.type), number: c.number }))}
                  selectedCardId={blueprintCardId}
                  onSelectCard={setBlueprintCardId}
                  commitRef={blueprintGraphCommitRef}
                  board={inner}
                />
              );
            }
            return (
              <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar-when-not-needed">
                <div className="flex justify-center py-2">{inner}</div>
              </div>
            );
          })()}
          <div className="absolute bottom-2 left-2 z-20 pointer-events-none max-w-[min(100%,280px)]">
            {isNumberMode && (
              <div className="text-[11px] text-gray-400 mb-1">
                Click a port and type digits only. Enter or Tab moves to the next port. Hold P to see port types.
              </div>
            )}
            <div className="text-[11px] font-medium">
              {portCountLines.length === 0 ? (
                <span className="text-gray-500">0 ports</span>
              ) : (
                <div className="flex flex-col items-start gap-0.5">
                  {portCountLines.map((line) => (
                    <span key={line.label} className="tabular-nums" style={{ color: line.color }}>
                      {line.count}x {line.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
          {cellTooltip ? (
            <PortHoverTooltip title={cellTooltip.title} lines={cellTooltip.lines} x={cellTooltip.x} y={cellTooltip.y} />
          ) : null}

            </div>
            {showElectrical && deviceModelDraft && (!isCreatingNew || (!!selectedManufacturer && !!selectedCategory)) && (
              <>
                <button
                  type="button"
                  onClick={toggleInfoPanel}
                  className="shrink-0 w-6 self-stretch border-l border-white/10 bg-slate-800/80 hover:bg-slate-700 text-gray-300 flex items-center justify-center"
                  title={showInfoPanel ? 'Hide info panel' : 'Show info panel'}
                >
                  {showInfoPanel ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                </button>
                {showInfoPanel && (
                  editorView === 'blueprint' ? (
                    <ElectricalInspector
                      draft={deviceModelDraft}
                      onChange={setDeviceModelDraft}
                      ports={liveElectricalPorts}
                      onPortsChange={(next) => stampFacedPortsOntoGrids(next)}
                      hoverPortId={hoverPortId}
                      onHoverPort={hoverElectricalPort}
                    />
                  ) : (
                    <div className="w-[480px] shrink-0 min-h-0 overflow-hidden border-l border-white/10 bg-black/20 pr-1">
                      <ElectricalModelPanel
                        draft={deviceModelDraft}
                        onChange={setDeviceModelDraft}
                        ports={liveElectricalPorts}
                        onPortsChange={(next) => stampFacedPortsOntoGrids(next)}
                        isDark
                        hoverPortId={hoverPortId}
                        onHoverPort={hoverElectricalPort}
                        compact
                      />
                    </div>
                  )
                )}
              </>
            )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3.5 border-t border-slate-700/50 flex items-center justify-between gap-3 bg-slate-800/50 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {standalone && (
              <button
                onClick={handleSaveCurrentAsTemplate}
                className="px-4 py-2 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors flex items-center gap-1.5"
                title="Save as new template"
              >
                <Save size={14} />
                Save as New Template
              </button>
            )}
            {showElectrical && !electricalValidation.ok && (
              <span className="text-sm text-red-400 truncate">
                {electricalValidation.errors[0] || 'Fix electrical errors before saving'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded bg-white/10 text-gray-200 hover:bg-white/15"
            >
              Cancel
            </button>
            {showDualSave && !standalone && !isCreatingNew ? (
              <>
                <button
                  onClick={() => handleSave('instance')}
                  disabled={!hasChanges || (showElectrical && !electricalValidation.ok)}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Save layout and electrical model on this placed equipment only"
                >
                  Save (this instance only)
                </button>
                <button
                  onClick={() => handleSave('library')}
                  disabled={!hasChanges || (showElectrical && !electricalValidation.ok)}
                  className="px-4 py-2 text-sm bg-slate-700 text-white border border-slate-500 rounded-md hover:bg-slate-600 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Save layout and electrical model as the library default for this equipment type"
                >
                  Save (Update Library)
                </button>
              </>
            ) : (
              <button
                onClick={() => handleSave()}
                disabled={!hasChanges || (showElectrical && !electricalValidation.ok) || (isCreatingNew && (!selectedManufacturer || !selectedCategory || !newEquipmentName.trim()))}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isCreatingNew ? 'Create Equipment' : (standalone ? 'Save' : 'Save')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Create Custom Template Modal */}
      {isCreatingTemplate && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 w-[450px] max-w-[90vw] max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Create Custom Template</h3>
              <button
                onClick={() => {
                  setIsCreatingTemplate(false);
                  setIsSavingCurrentAsTemplate(false);
                  setNewTemplateName('');
                  setNewTemplateCols(12);
                  setNewTemplateRows(2);
                  setNewTemplateDescription('');
                }}
                className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-300 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 p-4 overflow-auto">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., Small Router 8x2"
                    autoFocus
                  />
                </div>

                {isSavingCurrentAsTemplate ? (
                  <div className="p-2 bg-blue-900/30 border border-blue-700/50 rounded-md text-xs text-blue-300">
                    <div className="grid grid-cols-2 gap-3 mb-1.5">
                      <div>
                        <span className="text-[10px] text-gray-400">Columns:</span>
                        <span className="ml-1.5 font-semibold">{newTemplateCols}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400">Rows:</span>
                        <span className="ml-1.5 font-semibold">{newTemplateRows}</span>
                      </div>
                    </div>
                    <span className="text-[10px]">Using current canvas size ({newTemplateCols}×{newTemplateRows})</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Columns *
                      </label>
                      <input
                        type="number"
                        value={newTemplateCols}
                        onChange={(e) => setNewTemplateCols(parseInt(e.target.value) || 1)}
                        min="1"
                        max="64"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Rows *
                      </label>
                      <input
                        type="number"
                        value={newTemplateRows}
                        onChange={(e) => setNewTemplateRows(parseInt(e.target.value) || 1)}
                        min="1"
                        max="64"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., For small routers with 8 ports"
                  />
                </div>

                <div className="p-2 bg-slate-700/50 border border-slate-600 rounded-md text-[10px] text-gray-400">
                  <strong>Preview:</strong> {newTemplateCols}×{newTemplateRows} grid ({newTemplateCols * newTemplateRows} cells total)
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end gap-1.5">
              <button
                onClick={() => {
                  setIsCreatingTemplate(false);
                  setIsSavingCurrentAsTemplate(false);
                  setNewTemplateName('');
                  setNewTemplateCols(12);
                  setNewTemplateRows(2);
                  setNewTemplateDescription('');
                }}
                className="px-3 py-1.5 text-xs bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Manager Modal */}
      {isTemplateManagerOpen && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 w-[550px] max-w-[90vw] max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-base font-semibold text-white">Template Library</h3>
              <button
                onClick={() => setIsTemplateManagerOpen(false)}
                className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-300 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 p-4 overflow-auto">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-300">Preset Templates</h4>
                <span className="text-xs text-gray-400">Built-in</span>
              </div>
              
              <div className="mb-4 space-y-1.5">
                {Object.entries(PRESET_TEMPLATES).map(([key, template]) => (
                  <div
                    key={key}
                    className="p-2 bg-slate-700/50 border border-slate-600 rounded-md flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-sm text-white">{template.name}</div>
                      <div className="text-xs text-gray-400">
                        {template.cols}×{template.rows} - {template.description}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-slate-600 rounded">
                      Preset
                    </span>
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-300">Custom Templates</h4>
              </div>

              {Object.keys(customTemplates).length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">
                  No custom templates yet. Create one to get started!
                </div>
              ) : (
                <div className="space-y-1.5">
                  {Object.values(customTemplates).map((template) => (
                    <div
                      key={template.id}
                      className="p-2 bg-slate-700/50 border border-slate-600 rounded-md flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-sm text-white">{template.name}</div>
                        <div className="text-xs text-gray-400">
                          {template.cols}×{template.rows}
                          {template.description && ` - ${template.description}`}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Created: {new Date(template.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="p-1.5 text-red-400 hover:bg-red-900/30 rounded transition-colors"
                        title="Delete Template"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end">
              <button
                onClick={() => setIsTemplateManagerOpen(false)}
                className="px-3 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Naming Scheme Manager Modal */}
      {isSchemeManagerOpen && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 w-[550px] max-w-[90vw] max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-base font-semibold text-white">Naming Schemes</h3>
              <button
                onClick={() => {
                  setIsSchemeManagerOpen(false);
                  resetSchemeForm();
                }}
                className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-300 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 p-4 overflow-auto">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-300">Built-in Schemes</h4>
                <span className="text-xs text-gray-400">Read-only</span>
              </div>

              <div className="mb-4 space-y-1.5">
                {BUILT_IN_PORT_NAMING_SCHEMES.map((scheme) => (
                  <div
                    key={scheme.id}
                    className="p-2 bg-slate-700/50 border border-slate-600 rounded-md flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-sm text-white">{scheme.name}</div>
                      <div className="text-xs text-gray-400">{describeSchemePrefixes(scheme)}</div>
                    </div>
                    <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-slate-600 rounded">
                      Built-in
                    </span>
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-300">Custom Schemes</h4>
              </div>

              {Object.keys(customNamingSchemes).length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm mb-4">
                  No custom schemes yet. Create one below.
                </div>
              ) : (
                <div className="space-y-1.5 mb-4">
                  {Object.values(customNamingSchemes).map((scheme) => (
                    <div
                      key={scheme.id}
                      className="p-2 bg-slate-700/50 border border-slate-600 rounded-md flex items-center justify-between gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-white">{scheme.name}</div>
                        <div className="text-xs text-gray-400 truncate">{describeSchemePrefixes(scheme)}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => startEditScheme(scheme)}
                          className="p-1.5 text-gray-300 hover:bg-slate-600 rounded transition-colors"
                          title="Edit Scheme"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteNamingScheme(scheme.id)}
                          className="p-1.5 text-red-400 hover:bg-red-900/30 rounded transition-colors"
                          title="Delete Scheme"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-3 bg-slate-700/40 border border-slate-600 rounded-md space-y-3">
                <div className="text-sm font-semibold text-white">
                  {editingSchemeId ? 'Edit Scheme' : 'Create Scheme'}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Name</label>
                  <input
                    type="text"
                    value={newSchemeName}
                    onChange={(e) => setNewSchemeName(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., Cisco, UniFi"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {NAMING_SCHEME_PREFIX_KINDS.map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-300 mb-1">{label} prefix</label>
                      <input
                        type="text"
                        value={newSchemePrefixes[key] || ''}
                        onChange={(e) => setNewSchemePrefixes((prev) => ({ ...prev, [key]: e.target.value }))}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="(number only)"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-1.5">
                  {editingSchemeId && (
                    <button
                      onClick={resetSchemeForm}
                      className="px-3 py-1.5 text-xs bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={handleSaveNamingScheme}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                  >
                    {editingSchemeId ? 'Save Scheme' : 'Create Scheme'}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end">
              <button
                onClick={() => {
                  setIsSchemeManagerOpen(false);
                  resetSchemeForm();
                }}
                className="px-3 py-1.5 text-sm bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 w-[450px] max-w-[90vw] max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <FileText size={16} className="text-blue-400" />
                Export Equipment Layout
              </h3>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-300 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 p-4 overflow-auto">
              <div className="space-y-3">
                <div className="p-2 bg-blue-900/30 border border-blue-700/50 rounded-md text-xs text-blue-300">
                  <FileText size={12} className="inline mr-1.5" />
                  Export your equipment layout to a <strong>.npb</strong> file that can be shared or imported later.
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Equipment Name *
                  </label>
                  <input
                    type="text"
                    value={exportMetadata.name}
                    onChange={(e) => setExportMetadata(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g., Ubiquiti Switch 8"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={exportMetadata.description}
                    onChange={(e) => setExportMetadata(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    rows={2}
                    placeholder="Brief description of the equipment..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">
                      Manufacturer
                    </label>
                    <input
                      type="text"
                      value={exportMetadata.manufacturer}
                      onChange={(e) => setExportMetadata(prev => ({ ...prev, manufacturer: e.target.value }))}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="e.g., Ubiquiti"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">
                      Model
                    </label>
                    <input
                      type="text"
                      value={exportMetadata.model}
                      onChange={(e) => setExportMetadata(prev => ({ ...prev, model: e.target.value }))}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="e.g., US-8"
                    />
                  </div>
                </div>

                <div className="p-2 bg-slate-700/50 border border-slate-600 rounded-md text-[10px] text-gray-400">
                  <div className="font-medium mb-0.5">What will be exported:</div>
                  <ul className="list-disc list-inside space-y-0.5 ml-1.5">
                    <li>All port layouts ({isMultiFaceMode ? 'multi-face' : 'single-face'})</li>
                    <li>Custom templates used (if any)</li>
                    <li>Equipment metadata</li>
                    <li>Port types and positions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end gap-1.5">
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="px-3 py-1.5 text-xs bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium flex items-center gap-1.5"
                disabled={!exportMetadata.name.trim()}
              >
                <Download size={12} />
                Export to .npb
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Standalone Template Editor */}
      {isStandaloneEditorOpen && (
        <SimpleLayoutEditor
          onSave={() => {
            // Save is handled internally in standalone mode
          }}
          onClose={() => {
            setIsStandaloneEditorOpen(false);
            isOpeningStandalone.current = false;
          }}
          initialLayout={undefined}
          portCount={8}
          standalone={true}
        />
      )}


      {/* Save Confirmation Dialog */}
      {showSaveConfirmation && (
        <div 
          className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSaveConfirmation(false);
              setPendingSaveTarget(undefined);
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-2xl border border-slate-700/50 w-[400px] max-w-[92vw]"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-700/50 text-white font-semibold text-sm flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-400" />
              <span>{pendingSaveTarget === 'library' ? 'Update library layout & electrical model?' : pendingSaveTarget === 'instance' ? 'Overwrite this instance?' : 'Overwrite layout & electrical model?'}</span>
            </div>
            <div className="p-4 text-sm text-gray-300">
              {pendingSaveTarget === 'library'
                ? 'This will update the library layout and electrical model for this equipment type, and apply it to every instance already on the canvas. Matching ports keep their cables; ports that no longer exist fall back to the badge.'
                : pendingSaveTarget === 'instance'
                  ? 'This will overwrite the port layout and electrical model for this placed equipment only. The library and other instances will not change.'
                  : 'This will overwrite the existing port layout and electrical model. Are you sure you want to continue?'}
            </div>
            <div className="px-4 py-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end gap-2">
              <button 
                className="px-4 py-2 text-sm rounded bg-slate-700/50 hover:bg-slate-700 text-gray-300 transition-colors border border-slate-600" 
                onClick={() => {
                  setShowSaveConfirmation(false);
                  setPendingSaveTarget(undefined);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                onClick={() => performSave(pendingSaveTarget)}
              >
                {pendingSaveTarget === 'library' ? 'Update Library' : pendingSaveTarget === 'instance' ? 'Save Instance' : 'Save Layout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Custom Port Type Confirmation Dialog */}
      {showDeleteCustomPortConfirm && (
        <div 
          className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteCustomPortConfirm(false);
              setPortTypeToDelete(null);
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-2xl border border-slate-700/50 w-[400px] max-w-[92vw]"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-700/50 text-white font-semibold text-sm flex items-center gap-2">
              <AlertCircle size={16} className="text-red-400" />
              <span>Delete Custom Port Type?</span>
            </div>
            <div className="p-4 text-sm text-gray-300">
              Are you sure you want to delete this custom port type? This action cannot be undone. Any ports using this type will need to be reassigned.
            </div>
            <div className="px-4 py-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end gap-2">
              <button 
                className="px-4 py-2 text-sm rounded bg-slate-700/50 hover:bg-slate-700 text-gray-300 transition-colors border border-slate-600" 
                onClick={() => {
                  setShowDeleteCustomPortConfirm(false);
                  setPortTypeToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded bg-red-600 hover:bg-red-700 text-white transition-colors"
                onClick={confirmDeleteCustomPort}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Template Menu */}
      {isAddNewTemplateMenuOpen && addNewTemplateMenuPosition && createPortal(
        <div
          ref={addNewTemplateMenuRef}
          className="fixed bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-slate-700/50 z-[150] w-[360px] flex flex-col"
          style={{
            top: `${addNewTemplateMenuPosition.top}px`,
            left: `${addNewTemplateMenuPosition.left}px`,
            maxHeight: 'calc(100vh - 20px)',
            maxWidth: 'calc(100vw - 20px)'
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 p-3 border-b border-slate-700/50">
            <h3 className="text-sm font-semibold text-white">Create New Template</h3>
          </div>
          
          <div className="flex-1 p-4 overflow-y-auto overflow-x-hidden min-h-0" style={{ maxHeight: '400px' }}>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Small Router 8x2"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Columns *
                  </label>
                  <input
                    type="number"
                    value={newTemplateCols}
                    onChange={(e) => setNewTemplateCols(parseInt(e.target.value) || 1)}
                    min="1"
                    max="64"
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Rows *
                  </label>
                  <input
                    type="number"
                    value={newTemplateRows}
                    onChange={(e) => setNewTemplateRows(parseInt(e.target.value) || 1)}
                    min="1"
                    max="64"
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newTemplateDescription}
                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs border border-slate-600 rounded-md bg-slate-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., For small routers with 8 ports"
                />
              </div>

              {/* Visual Preview */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  Preview
                </label>
                {(() => {
                  const padding = 8; // Padding on all sides
                  const cellSize = 12;
                  const gap = 2;
                  // Calculate grid content size: cells + gaps between them
                  const gridContentWidth = newTemplateCols * cellSize + (newTemplateCols - 1) * gap;
                  const gridContentHeight = newTemplateRows * cellSize + (newTemplateRows - 1) * gap;
                  // Total size including padding on all sides
                  const totalWidth = gridContentWidth + padding * 2;
                  const totalHeight = gridContentHeight + padding * 2;
                  
                  const maxWidth = 320; // Max width of container minus padding
                  const maxHeight = 180; // Max height of container minus padding
                  const scaleX = maxWidth / totalWidth;
                  const scaleY = maxHeight / totalHeight;
                  const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
                  const scaledWidth = totalWidth * scale;
                  const scaledHeight = totalHeight * scale;
                  
                  return (
                    <div className="bg-slate-900/50 border border-slate-600 rounded-md p-3 flex items-center justify-center" style={{ minHeight: '60px' }}>
                      {/* Wrapper div with scaled dimensions and white background to cover all cells with uniform padding */}
                      <div
                        className="bg-white rounded"
                        style={{
                          width: `${scaledWidth}px`,
                          height: `${scaledHeight}px`,
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${newTemplateCols}, 1fr)`,
                            gridTemplateRows: `repeat(${newTemplateRows}, 1fr)`,
                            gap: `${gap}px`,
                            padding: `${padding}px`,
                            width: `${totalWidth}px`,
                            height: `${totalHeight}px`,
                            transform: scale < 1 ? `scale(${scale})` : 'scale(1)',
                            transformOrigin: 'top left',
                            position: 'absolute',
                            top: 0,
                            left: 0
                          }}
                        >
                          {Array.from({ length: newTemplateRows * newTemplateCols }).map((_, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-500 border border-gray-400 rounded-sm"
                              style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <p className="text-[10px] text-gray-400 mt-1 text-center">
                  {newTemplateCols}×{newTemplateRows} grid ({newTemplateCols * newTemplateRows} cells)
                </p>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 p-3 border-t border-slate-700/50 bg-slate-800/50 flex justify-end gap-1.5">
            <button
              onClick={() => {
                setIsAddNewTemplateMenuOpen(false);
                setAddNewTemplateMenuPosition(null);
                setNewTemplateName('');
                setNewTemplateCols(12);
                setNewTemplateRows(2);
                setNewTemplateDescription('');
              }}
              className="px-3 py-1.5 text-xs bg-slate-700/50 text-gray-300 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTemplate}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!newTemplateName.trim()}
            >
              Create Template
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SimpleLayoutEditor;


